import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import API from '../api';
import ChatWindow from './ChatWindow';
import ExamRoom from './ExamRoom';
import { unregisterPushSubscription } from '../push';
import { UserMaterials } from './Materials';
import { generateStudentPDF } from '../utils/studentReport';



const localizer = momentLocalizer(moment);

/* ── helpers ── */
// Convert IST time to user's local timezone for display
function toLocalTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return timeStr || '';
  try {
    const istDate = new Date(`${dateStr}T${timeStr}+05:30`);
    const h = istDate.getHours();
    const m = istDate.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return timeStr; }
}
function toLocalDatetime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : '+05:30'));
    const h = d.getHours(), m = d.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${d.toLocaleDateString()} ${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return isoStr; }
}
const fmtTime = (t, dateStr) => {
  if (!t) return '—';
  // Always treat stored time as IST; use today's date if none provided
  const ref = dateStr || new Date().toISOString().split('T')[0];
  return toLocalTime(ref, t);
};
const fmtDate = (d) => { if (!d) return '—'; const [y,mo,day]=d.split('-'); return `${day}-${mo}-${y}`; };

// ── 12-hour TimePicker ────────────────────────────────────────────────────────
function TimePicker12h({ value, onChange, className, style }) {
  const toDisplay = (v) => {
    if (!v) return { h: '12', m: '00', period: 'AM' };
    const [hh, mm] = v.split(':');
    let h = parseInt(hh);
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return { h: String(h), m: mm || '00', period };
  };
  const { h, m, period } = toDisplay(value);
  const to24 = (hStr, mStr, p) => {
    let hr = parseInt(hStr);
    if (p === 'AM' && hr === 12) hr = 0;
    if (p === 'PM' && hr !== 12) hr += 12;
    return `${String(hr).padStart(2,'0')}:${String(mStr).padStart(2,'0')}`;
  };
  const sel = { padding:'9px 8px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, fontFamily:'inherit', background:'#fff', ...style };
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
      <select style={sel} value={h} onChange={e => onChange(to24(e.target.value, m, period))}>
        {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}
      </select>
      <span style={{ fontWeight:700, color:'#64748b' }}>:</span>
      <select style={sel} value={m} onChange={e => onChange(to24(h, e.target.value, period))}>
        {['00','05','10','15','20','25','30','35','40','45','50','55'].map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <select style={{ ...sel, paddingLeft:6, paddingRight:6 }} value={period} onChange={e => onChange(to24(h, m, e.target.value))}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

/* ── style helpers ── */
const card = (extra={}) => ({ background:'#fff', borderRadius:20, border:'1px solid #e8edf8', boxShadow:'0 4px 24px rgba(37,84,184,0.07)', padding:28, transition:'box-shadow 0.25s, transform 0.25s', ...extra });
const pill = (active,color='#2554b8') => ({ padding:'8px 22px', borderRadius:50, border:`2px solid ${active?color:'#e2e8f6'}`, background:active?color:'#fff', color:active?'#fff':'#64748b', fontWeight:700, fontSize:13, cursor:'pointer', transition:'all 0.2s ease', outline:'none', boxShadow:active?`0 4px 14px ${color}44`:'none' });

function useUnreadChats() {
  const [unreadRooms, setUnreadRooms] = useState({});
  const check = useCallback(() => {
    API.get('/chat/rooms').then(r => {
      const map = {};
      (r.data || []).forEach(room => {
        const seen = parseInt(localStorage.getItem(`chat_seen_${room.room_id}`) || '0');
        map[room.room_id] = (room.last_message_id || 0) > seen;
      });
      setUnreadRooms(map);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [check]);
  return [unreadRooms, check];
}

function LiveClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      let h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      const period = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      setTime(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${period}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontWeight:600, fontFamily:'monospace', letterSpacing:0.5 }}>{time}</span>;
}

/* ═══════════════════════════════════════════════════
   GLOBAL STYLES (injected once)
═══════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  @keyframes sidebarSlide { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
  @keyframes bounceIn { 0%{transform:scale(.82);opacity:0} 60%{transform:scale(1.06)} 100%{transform:scale(1);opacity:1} }
  @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
  @keyframes shimmer { 0%{opacity:.5} 50%{opacity:1} 100%{opacity:.5} }
  @keyframes ripple { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(2.4);opacity:0} }
  .s-nav-item { transition:all 0.22s cubic-bezier(.4,0,.2,1); position:relative; overflow:hidden; }
  .s-nav-item::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#fff; border-radius:0 3px 3px 0; transform:scaleY(0); transition:transform .2s ease; }
  .s-nav-item.s-active::before { transform:scaleY(1); }
  .s-nav-item:hover { background:rgba(255,255,255,0.12) !important; padding-left:26px !important; }
  .s-nav-item.s-active { background:rgba(255,255,255,0.18) !important; padding-left:26px !important; }
  .s-card-hover:hover { box-shadow:0 14px 44px rgba(37,84,184,0.17) !important; transform:translateY(-4px) !important; }
  .s-btn { transition:all 0.2s ease; }
  .s-btn:hover { filter:brightness(1.08); transform:translateY(-2px); box-shadow:0 8px 22px rgba(37,84,184,0.28); }
  .s-btn:active { transform:scale(0.97); }
  .s-tr:hover td { background:#f5f8ff !important; }
  .s-row { animation:fadeUp 0.3s ease both; }
  .s-stat:hover { transform:translateY(-5px); box-shadow:0 18px 44px rgba(37,84,184,0.18) !important; }
  .s-stat { transition:all 0.28s cubic-bezier(.4,0,.2,1); }
  .status-dot-s { width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px; }
  .status-dot-s.online { background:#16a34a; box-shadow:0 0 0 3px rgba(22,163,74,0.2); animation:pulse 2s infinite; }
  .s-gradient-text { background:linear-gradient(135deg,#1a3c8f,#3b82f6); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
  .s-page { animation:fadeUp 0.38s cubic-bezier(.4,0,.2,1) both; }
  .s-leave-card { transition:all 0.22s; cursor:pointer; }
  .s-leave-card:hover { transform:translateY(-4px); box-shadow:0 10px 30px rgba(37,84,184,0.17) !important; }
  .s-leave-card.chosen { border-color:#2554b8 !important; }
  .s-form-inp:focus { border-color:#2554b8 !important; box-shadow:0 0 0 3px rgba(37,84,184,0.12) !important; outline:none; }
  .s-companion:hover { transform:translateY(-5px); box-shadow:0 14px 40px rgba(37,84,184,0.16) !important; }
  .s-companion { transition:all 0.26s; }
  .floating { animation:float 3.5s ease-in-out infinite; }
  .floating-delay { animation:float 3.5s ease-in-out 1.2s infinite; }
  .shimmer { animation:shimmer 2s ease-in-out infinite; }

  /* ── react-big-calendar modern overrides ── */
  .rbc-calendar { font-family:'Plus Jakarta Sans',sans-serif; }
  .rbc-toolbar { padding:12px 16px; background:linear-gradient(135deg,#f8faff,#eef2ff); border-bottom:1px solid #e2e8f6; flex-wrap:wrap; gap:8px; }
  .rbc-toolbar button { border-radius:8px; border:1.5px solid #e2e8f6; background:#fff; color:#2554b8; font-weight:600; font-size:12px; padding:6px 14px; cursor:pointer; transition:all 0.18s; font-family:inherit; }
  .rbc-toolbar button:hover { background:#2554b8; color:#fff; border-color:#2554b8; }
  .rbc-toolbar button.rbc-active { background:#2554b8 !important; color:#fff !important; border-color:#2554b8 !important; box-shadow:0 3px 10px rgba(37,84,184,0.3); }
  .rbc-toolbar button.rbc-active:hover { filter:brightness(1.1); }
  .rbc-toolbar-label { font-weight:800; font-size:16px; color:#0f1f45; }
  .rbc-header { padding:10px 4px; background:#f8faff; font-weight:700; font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:0.6px; border-bottom:1px solid #e2e8f6 !important; }
  .rbc-month-view { border:none; border-top:1px solid #e2e8f6; }
  .rbc-month-row { border-top:1px solid #f1f5f9; }
  .rbc-row-content { height:100%; }
  .rbc-month-view .rbc-event { margin:1px 2px !important; padding:3px 7px !important; }
  .rbc-day-bg { border-left:1px solid #f1f5f9 !important; }
  .rbc-today { background:rgba(37,84,184,0.05) !important; }
  .rbc-off-range-bg { background:#fafbfe !important; }
  .rbc-off-range .rbc-date-cell a { color:#cbd5e1; }
  .rbc-date-cell { padding:6px 10px; font-size:12px; font-weight:600; color:#475569; }
  .rbc-date-cell.rbc-now a { color:#2554b8; font-weight:800; }
  .rbc-event { overflow:hidden !important; border:none !important; padding:4px 8px !important; border-radius:6px !important; box-shadow:0 2px 6px rgba(0,0,0,0.18) !important; }
  .rbc-event:focus { outline:none !important; }
  .rbc-event.rbc-selected { opacity:0.92; box-shadow:0 4px 12px rgba(0,0,0,0.28) !important; }
  .rbc-evt-scheduled { background-color:#2554b8 !important; }
  .rbc-evt-completed { background-color:#16a34a !important; }
  .rbc-evt-cancelled { background-color:#dc2626 !important; }
  .rbc-evt-exam { background-color:#7c3aed !important; }
  .rbc-evt-leave { background-color:#f97316 !important; }
  .rbc-event-content { overflow:hidden !important; width:100% !important; }
  .rbc-event-label { font-size:10px !important; font-weight:600 !important; opacity:0.9; white-space:nowrap; }
  .rbc-time-view .rbc-event { min-height:36px !important; }
  .rbc-show-more { color:#2554b8; font-size:11px; font-weight:700; background:rgba(37,84,184,0.1); padding:2px 10px; border-radius:20px; border:1px solid rgba(37,84,184,0.2); cursor:pointer; display:inline-block; margin:1px 2px; }
  .rbc-show-more:hover { background:rgba(37,84,184,0.18); }
  .rbc-time-view { border:none; border-top:1px solid #e2e8f6; }
  .rbc-time-header { background:#f8faff; border-bottom:1px solid #e2e8f6; }
  .rbc-time-header-content { border-left:1px solid #e2e8f6; }
  .rbc-time-content { border-top:1px solid #e2e8f6; }
  .rbc-timeslot-group { border-bottom:1px solid #f8faff; }
  .rbc-time-slot { font-size:11px; color:#94a3b8; font-weight:500; }
  .rbc-current-time-indicator { background:#ef4444; height:2px; border-radius:2px; }
  .rbc-agenda-view table.rbc-agenda-table { border-radius:10px; overflow:hidden; border:1px solid #e2e8f6; }
  .rbc-agenda-view table.rbc-agenda-table thead { background:#f8faff; }
  .rbc-agenda-view table.rbc-agenda-table thead th { padding:10px 16px; font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; }
  .rbc-agenda-view table.rbc-agenda-table tbody > tr > td { padding:10px 16px; font-size:13px; border-top:1px solid #f1f5f9; }
  .rbc-slot-selection { background:rgba(37,84,184,0.1); border:1px solid rgba(37,84,184,0.3); }
`;

/* ═══════════════════════════════════════════════════
   NAV ITEMS
═══════════════════════════════════════════════════ */
const NAV = [
  { id:'dashboard',   icon:'🏠', label:'Dashboard'          },
  { id:'profile',     icon:'👤', label:'Profile'            },
  { id:'enrollment',  icon:'🎓', label:'Enrollment Details' },
  { id:'payment',     icon:'💳', label:'Payment Information'},
  { id:'academic',    icon:'📊', label:'Academic Details'   },
  { id:'schedule',    icon:'📅', label:'Schedules'          },
  { id:'exam',        icon:'✏️',  label:'Attend Exam'        },
  { id:'leave',       icon:'📝', label:'Leave Request Form' },
  { id:'help',        icon:'❓', label:'Help & Support'     },
  { id:'chat',        icon:'💬', label:'Chat'               },
  { id:'classreq',    icon:'📋', label:'Class Request'      },
  { id:'homework',    icon:'📚', label:'Homework'           },
  { id:'feedback',    icon:'⭐', label:'Give Feedback'      },
  { id:'materials',   icon:'📖', label:'Study Materials'   },
];

/* ═══════════════════════════════════════════════════
   MULTI-ACCOUNT UTILITIES
═══════════════════════════════════════════════════ */
const ACCOUNTS_KEY = 'saved_student_accounts';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getAccounts() {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const accounts = JSON.parse(raw);
    // Keep all accounts — expired ones will prompt re-login on switch
    return accounts.filter(a => a.user_id && a.name);
  } catch { return []; }
}

function isTokenExpired(account) {
  return !account.token || (Date.now() - (account.stored_at || 0)) >= TTL_MS;
}

function saveAccount(userId, name, token, email = '') {
  try {
    const existing = getAccounts().find(a => a.user_id === userId);
    const accounts = getAccounts().filter(a => a.user_id !== userId);
    accounts.push({ user_id: userId, name, email: email || existing?.email || '', token, stored_at: Date.now() });
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch {}
}

function removeAccount(userId) {
  try {
    const accounts = getAccounts().filter(a => a.user_id !== userId);
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch {}
}

async function switchToAccount(account) {
  // Save current account first (with its current token) so we can come back to it
  const curId = parseInt(localStorage.getItem('user_id') || '0');
  const curName = localStorage.getItem('name') || '';
  const curToken = localStorage.getItem('access_token') || '';
  if (curId && curToken) saveAccount(curId, curName, curToken);

  // Ask backend to re-issue the auth cookie for the target account.
  // Without this, the httpOnly cookie still identifies the previous user
  // because the backend reads the cookie first.
  let issuedToken = null;
  let issuedCsrf = null;
  try {
    const { default: API } = await import('../api');
    const res = await API.post('/auth/switch-account', { access_token: account.token });
    issuedToken = res.data?.access_token || null;
    issuedCsrf = res.data?.csrf_token || null;
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || '';
    if (status === 401) {
      // Saved token is no longer valid — drop it and ask user to re-login
      removeAccount(account.user_id);
      alert('This account session expired. Please add it again to switch.');
    } else {
      alert(detail || 'Could not switch — please try again.');
    }
    return;
  }
  if (!issuedToken) {
    alert('Switch failed: server did not return a new session token.');
    return;
  }

  // Load new account into client storage
  sessionStorage.setItem('access_token', issuedToken);
  localStorage.setItem('access_token', issuedToken);
  localStorage.setItem('access_token_at', String(Date.now()));
  localStorage.setItem('name', account.name);
  localStorage.setItem('user_id', String(account.user_id));
  localStorage.setItem('role', 'student');
  localStorage.setItem('force_password_change', '0');
  if (issuedCsrf) sessionStorage.setItem('csrf_token', issuedCsrf);
  window.location.reload();
}

/* ═══════════════════════════════════════════════════
   SIDEBAR
═══════════════════════════════════════════════════ */
function Sidebar({ active, setActive, unreadCount, sidebarOpen, setSidebarOpen }) {
  const name = localStorage.getItem('name') || 'Student';
  const currentUserId = parseInt(localStorage.getItem('user_id') || '0');
  const [showSignout, setShowSignout] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [reAuthAccount, setReAuthAccount] = useState(null); // account needing re-login
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [hovered, setHovered] = useState(null);
  const [accounts, setAccounts] = useState(() => getAccounts().filter(a => a.user_id !== currentUserId));

  const logout = async () => {
    removeAccount(currentUserId);
    await unregisterPushSubscription();
    try { await API.post('/auth/logout'); } catch(_){}
    localStorage.clear(); sessionStorage.clear();
    window.location.href = '/login';
  };

  const handleAddAccount = async () => {
    if (!addEmail.trim() || !addPassword) { setAddError('Enter email and password'); return; }
    setAddLoading(true); setAddError('');
    try {
      const res = await API.post('/auth/add-account', { email: addEmail.trim(), password: addPassword });
      const { role, name: newName, user_id, access_token } = res.data;
      // Security: only student accounts allowed
      if (role !== 'student') { setAddError('Only student accounts can be added here.'); return; }
      // Security: can't add yourself
      if (user_id === currentUserId) { setAddError('This is already your current account.'); return; }
      saveAccount(user_id, newName, access_token, addEmail.trim());
      setAccounts(getAccounts().filter(a => a.user_id !== currentUserId));
      // Re-register push subscription so the new sibling account is included
      // alongside the existing one(s). The backend endpoint handles multiple
      // tokens atomically and each token authenticates its own user.
      try {
        const { registerPushSubscription } = await import('../push');
        await registerPushSubscription();
      } catch {}
      setShowAddAccount(false); setAddEmail(''); setAddPassword('');
    } catch(e) {
      const detail = e?.response?.data?.detail || 'Login failed. Check credentials.';
      setAddError(typeof detail === 'string' ? detail : 'Login failed.');
    } finally { setAddLoading(false); }
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`} style={{ width:252, background:'linear-gradient(175deg,#1a3c8f 0%,#0f2460 60%,#0a1a45 100%)', display:'flex', flexDirection:'column', flexShrink:0, boxShadow:'4px 0 32px rgba(10,26,69,0.35)' }}>
        {/* Decorative blobs */}
        <div style={{ position:'absolute', top:-50, right:-50, width:150, height:150, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:100, left:-40, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,0.03)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'40%', right:-20, width:80, height:80, borderRadius:'50%', background:'rgba(59,130,246,0.08)', pointerEvents:'none' }} />

        {/* Brand */}
        <div style={{ padding:'24px 20px 18px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:11 }}>
            <div style={{ width:46, height:46, borderRadius:14, background:'#ffffff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid rgba(255,255,255,0.3)', overflow:'hidden', padding:4 }}>
              <img src="/logo.png" alt="logo" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
            </div>
            <div>
              <div style={{ color:'#fff', fontWeight:800, fontSize:13, letterSpacing:0.3 }}>Sapience Edu Connect</div>
              <div style={{ color:'rgba(255,255,255,0.42)', fontSize:9.5, fontWeight:500, marginTop:1 }}>Connecting minds shaping futures</div>
              <LiveClock />
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'12px 8px', overflowY:'auto' }}>
          {NAV.map((item, i) => {
            const isActive = active === item.id;
            return (
              <div key={item.id} className={`s-nav-item ${isActive?'s-active':''}`}
                onClick={() => { setActive(item.id); setSidebarOpen(false); }}
                onMouseEnter={() => setHovered(item.id)} onMouseLeave={() => setHovered(null)}
                style={{ display:'flex', alignItems:'center', gap:11, padding:'10px 14px', borderRadius:12, margin:'2px 0', cursor:'pointer', color:isActive?'#fff':'rgba(255,255,255,0.58)', fontWeight:isActive?700:500, fontSize:13, animation:`fadeUp 0.3s ease ${i*0.04}s both` }}>
                <span style={{ fontSize:15, width:20, textAlign:'center', transition:'transform 0.2s', transform:hovered===item.id?'scale(1.2)':'scale(1)' }}>{item.icon}</span>
                <span style={{ flex:1 }}>{item.label}</span>
                {item.id === 'chat' && unreadCount > 0 && !isActive && (
                  <span style={{ background:'#dc2626', color:'#fff', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:800, flexShrink:0 }}>{unreadCount}</span>
                )}
                {isActive && <div style={{ marginLeft:'auto', width:6, height:6, borderRadius:'50%', background:'#93c5fd', flexShrink:0 }} />}
              </div>
            );
          })}
          {/* Signout */}
          <div className="s-nav-item" onClick={() => setShowSignout(true)}
            style={{ display:'flex', alignItems:'center', gap:11, padding:'10px 14px', borderRadius:12, margin:'12px 0 2px', cursor:'pointer', color:'rgba(252,165,165,0.85)', fontWeight:500, fontSize:13, borderTop:'1px solid rgba(255,255,255,0.07)', paddingTop:14, marginTop:12 }}>
            <span style={{ fontSize:15 }}>↪</span> Signout
          </div>
        </nav>

        {/* Account switcher */}
        <div style={{ padding:'10px 12px 0' }}>
          {accounts.length > 0 && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8, marginBottom:6, paddingLeft:4 }}>Switch Account</div>
              {accounts.map(acc => (
                <div key={acc.user_id} onClick={() => {
                    if (isTokenExpired(acc)) {
                      setReAuthAccount(acc); setAddPassword(''); setAddError('');
                    } else {
                      switchToAccount(acc);
                    }
                  }}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:10, cursor:'pointer', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)', marginBottom:4 }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#a78bfa,#c4b5fd)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:12, color:'#1a3c8f', flexShrink:0 }}>{acc.name[0]?.toUpperCase()}</div>
                  <span style={{ color:'rgba(255,255,255,0.8)', fontSize:12, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.name}</span>
                  <span style={{ fontSize:10, color: isTokenExpired(acc) ? 'rgba(252,165,165,0.7)' : 'rgba(255,255,255,0.4)' }}>
                    {isTokenExpired(acc) ? '🔑 re-login' : 'tap to switch'}
                  </span>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { setShowAddAccount(true); setAddEmail(''); setAddPassword(''); setAddError(''); }}
            style={{ width:'100%', padding:'7px', borderRadius:10, border:'1px dashed rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.5)', fontWeight:600, fontSize:12, cursor:'pointer', marginBottom:10 }}>
            + Add Account
          </button>
        </div>

        {/* User chip */}
        <div style={{ padding:'0 12px 16px', borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'rgba(255,255,255,0.08)', borderRadius:14, border:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#60a5fa,#93c5fd)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, color:'#1a3c8f', flexShrink:0 }}>{name[0]?.toUpperCase()}</div>
            <div style={{ flex:1, overflow:'hidden' }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
              <div style={{ display:'flex', alignItems:'center', fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:1 }}>
                <span className="status-dot-s online" /> Student
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Account Modal */}
      {showAddAccount && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.65)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000, padding:16 }}
          onClick={() => setShowAddAccount(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:380, boxShadow:'0 30px 80px rgba(0,0,0,0.25)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ textAlign:'center', marginBottom:22 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>👤</div>
              <h3 style={{ fontSize:18, fontWeight:800, color:'#0f1f45', margin:0 }}>Add Another Account</h3>
              <p style={{ fontSize:12, color:'#64748b', marginTop:6 }}>Enter the other student's login credentials</p>
            </div>
            {addError && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:13, fontWeight:600, marginBottom:14, border:'1px solid #fca5a5' }}>{addError}</div>}
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Email Address</label>
              <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)}
                placeholder="student@example.com" autoComplete="off"
                style={{ width:'100%', padding:'10px 14px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Password</label>
              <input type="password" value={addPassword} onChange={e => setAddPassword(e.target.value)}
                placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handleAddAccount()}
                style={{ width:'100%', padding:'10px 14px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowAddAccount(false)}
                style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={handleAddAccount} disabled={addLoading}
                style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background: addLoading ? '#94a3b8' : 'linear-gradient(135deg,#1a3c8f,#2554b8)', color:'#fff', fontWeight:700, fontSize:14, cursor: addLoading ? 'not-allowed' : 'pointer' }}>
                {addLoading ? 'Verifying…' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-auth modal — when saved account token expires */}
      {reAuthAccount && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.65)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000, padding:16 }}
          onClick={() => setReAuthAccount(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:360, boxShadow:'0 30px 80px rgba(0,0,0,0.25)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg,#a78bfa,#c4b5fd)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:22, color:'#1a3c8f', margin:'0 auto 10px' }}>{reAuthAccount.name[0]?.toUpperCase()}</div>
              <h3 style={{ fontSize:17, fontWeight:800, color:'#0f1f45', margin:0 }}>{reAuthAccount.name}</h3>
              <p style={{ fontSize:12, color:'#64748b', marginTop:6 }}>Session expired. Enter password to continue.</p>
            </div>
            {addError && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:13, fontWeight:600, marginBottom:14, border:'1px solid #fca5a5' }}>{addError}</div>}
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Password</label>
              <input type="password" value={addPassword} onChange={e => setAddPassword(e.target.value)}
                placeholder="••••••••" autoFocus
                onKeyDown={async e => {
                  if (e.key !== 'Enter') return;
                  setAddLoading(true); setAddError('');
                  try {
                    const res = await API.post('/auth/add-account', { email: reAuthAccount.email || '', password: addPassword });
                    const { access_token, name: newName } = res.data;
                    saveAccount(reAuthAccount.user_id, newName || reAuthAccount.name, access_token);
                    switchToAccount({ ...reAuthAccount, token: access_token, stored_at: Date.now() });
                  } catch { setAddError('Incorrect password. Please try again.'); }
                  finally { setAddLoading(false); }
                }}
                style={{ width:'100%', padding:'10px 14px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setReAuthAccount(null)}
                style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:700, fontSize:14, cursor:'pointer' }}>Cancel</button>
              <button disabled={addLoading} onClick={async () => {
                  setAddLoading(true); setAddError('');
                  try {
                    const res = await API.post('/auth/add-account', { email: reAuthAccount.email || '', password: addPassword });
                    const { access_token, name: newName } = res.data;
                    saveAccount(reAuthAccount.user_id, newName || reAuthAccount.name, access_token);
                    switchToAccount({ ...reAuthAccount, token: access_token, stored_at: Date.now() });
                  } catch { setAddError('Incorrect password. Please try again.'); }
                  finally { setAddLoading(false); }
                }}
                style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background: addLoading ? '#94a3b8' : 'linear-gradient(135deg,#1a3c8f,#2554b8)', color:'#fff', fontWeight:700, fontSize:14, cursor: addLoading ? 'not-allowed' : 'pointer' }}>
                {addLoading ? 'Verifying…' : 'Switch Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signout modal */}
      {showSignout && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={() => setShowSignout(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:40, width:360, textAlign:'center', boxShadow:'0 30px 80px rgba(0,0,0,0.2)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ fontSize:54, marginBottom:12 }}>👋</div>
            <h3 style={{ fontSize:20, fontWeight:800, color:'#0f1f45', marginBottom:8 }}>Sign Out?</h3>
            <p style={{ color:'#64748b', marginBottom:28, fontSize:14 }}>Are you sure you want to end your session?</p>
            <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
              <button className="s-btn" onClick={() => setShowSignout(false)} style={{ padding:'10px 24px', borderRadius:12, border:'2px solid #e2e8f6', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:14 }}>Cancel</button>
              <button className="s-btn" onClick={logout} style={{ padding:'10px 24px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}>Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   DASHBOARD
═══════════════════════════════════════════════════ */
function Dashboard({ setActive, setAcademicInitTab }) {
  const [profile, setProfile] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [tests, setTests] = useState([]);
  const [scheduledTests, setScheduledTests] = useState([]);
  const [myHours, setMyHours] = useState(null);
  const [emergencyStatus, setEmergencyStatus] = useState(null);
  const [emergencyModal, setEmergencyModal] = useState(false);
  const [emergencyScheduleId, setEmergencyScheduleId] = useState('');
  const [emergencyReason, setEmergencyReason] = useState('');
  const [emergencyMsg, setEmergencyMsg] = useState('');
  const name = localStorage.getItem('name') || 'Student';

  useEffect(() => {
    API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {});
    API.get('/schedule/').then(r => setSchedules(r.data)).catch(() => {});
    API.get('/tests/my-grades').then(r => setTests(r.data)).catch(() => {});
    API.get('/tests/scheduled').then(r => setScheduledTests(r.data || [])).catch(() => {});
    API.get('/payments/my-hours').then(r => setMyHours(r.data)).catch(() => {});
    API.get('/emergency/status').then(r => setEmergencyStatus(r.data)).catch(() => {});
  }, []);

  const subjects = profile?.selected_subjects ? profile.selected_subjects.split(',').map(s=>s.trim()).filter(Boolean) : [];
  const upcoming = schedules.filter(s => s.status==='scheduled' && new Date(`${s.date}T${s.start_time}+05:30`) >= new Date()).sort((a,b) => new Date(`${a.date}T${a.start_time}+05:30`) - new Date(`${b.date}T${b.start_time}+05:30`)).slice(0,6);
  const upcomingTests = tests.filter(t => t.marks_obtained === null && new Date(t.test_date) >= new Date()).sort((a,b) => new Date(a.test_date)-new Date(b.test_date)).slice(0,3);
  // Merge scheduledTests that aren't already in upcomingTests
  const scheduledTestIds = new Set(upcomingTests.map(t => t.test_id));
  const extraScheduledTests = scheduledTests.filter(t => !scheduledTestIds.has(t.test_id) && !t.has_grade).slice(0,3);

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      {/* Hero banner */}
      <div style={{ borderRadius:24, padding:'32px 36px', marginBottom:26, background:'linear-gradient(135deg,#1a3c8f 0%,#2554b8 45%,#3b82f6 100%)', backgroundSize:'200% 200%', animation:'gradientShift 9s ease infinite', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-20, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-30, right:120, width:130, height:130, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:20, right:60, fontSize:64, opacity:0.15, userSelect:'none' }} className="floating">🎓</div>
        <div style={{ position:'absolute', bottom:10, right:20, fontSize:40, opacity:0.12, userSelect:'none' }} className="floating-delay">📚</div>
        <div style={{ position:'relative', zIndex:1 }}>
          <p style={{ color:'rgba(255,255,255,0.72)', fontSize:13, fontWeight:600, marginBottom:6 }}>👋 Welcome back,</p>
          <h2 style={{ color:'#fff', fontSize:28, fontWeight:800, letterSpacing:-0.5, marginBottom:8 }}>{name}!</h2>
          <p style={{ color:'rgba(255,255,255,0.65)', fontSize:13, maxWidth:460 }}>Here's your learning overview for today. Keep up the great work!</p>
        </div>
      </div>

      {/* Emergency status */}
      {emergencyStatus !== null && (
        <div style={{ marginBottom:18 }}>
          {emergencyStatus?.emergency_enabled ? (
            <button className="s-btn" onClick={() => { setEmergencyModal(true); setEmergencyMsg(''); setEmergencyScheduleId(''); setEmergencyReason(''); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}>
              🚨 Emergency Cancel
            </button>
          ) : (
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'11px 22px', borderRadius:14, background:'#f1f5f9', color:'#94a3b8', fontWeight:700, fontSize:13, border:'1.5px solid #e2e8f0' }}>
              🚫 Emergency unavailable — contact admin
            </div>
          )}
        </div>
      )}

      {/* Emergency cancel modal */}
      {emergencyModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
          onClick={() => setEmergencyModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:36, width:440, boxShadow:'0 30px 80px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize:18, fontWeight:800, color:'#dc2626', marginBottom:8 }}>🚨 Emergency Cancel</h3>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:20 }}>Select the upcoming class you need to cancel and provide a reason.</p>
            {emergencyMsg && (
              <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, background: emergencyMsg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: emergencyMsg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight:700, fontSize:13 }}>
                {emergencyMsg}
              </div>
            )}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Scheduled Class</label>
              <select value={emergencyScheduleId} onChange={e => setEmergencyScheduleId(e.target.value)}
                style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}>
                <option value="">— Select a class —</option>
                {upcoming.map(s => (
                  <option key={s.id} value={s.id}>{s.subject} · {fmtDate(s.date)} {s.start_time}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Reason</label>
              <textarea value={emergencyReason} onChange={e => setEmergencyReason(e.target.value)}
                rows={3} placeholder="Briefly describe the reason…"
                style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="s-btn" onClick={() => setEmergencyModal(false)}
                style={{ padding:'10px 22px', borderRadius:12, border:'2px solid #e2e8f6', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:13 }}>Cancel</button>
              <button className="s-btn"
                style={{ padding:'10px 22px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#dc2626,#ef4444)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:'0 4px 14px rgba(220,38,38,0.35)' }}
                onClick={async () => {
                  if (!emergencyScheduleId) { setEmergencyMsg('Please select a class'); return; }
                  if (!emergencyReason.trim()) { setEmergencyMsg('Please provide a reason'); return; }
                  try {
                    await API.post('/emergency/use', { schedule_id: parseInt(emergencyScheduleId), reason: emergencyReason.trim() });
                    setEmergencyMsg('✓ Emergency cancel used. Contact admin to re-enable.');
                    setEmergencyStatus({ emergency_enabled: false });
                  } catch (e) {
                    setEmergencyMsg(e.response?.data?.detail || 'Error processing emergency cancel');
                  }
                }}>
                Confirm Emergency Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top 3 info cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:18, marginBottom:26 }}>
        {/* Chosen Package */}
        <div className="s-card-hover" style={{ ...card({ background:'linear-gradient(135deg,#6366f1,#8b5cf6)', border:'none', padding:'24px', overflow:'hidden', position:'relative', cursor:'pointer' }), animation:'fadeUp 0.35s ease 0s both' }} onClick={() => setActive('enrollment')}>
          <div style={{ position:'absolute', top:-20, right:-20, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.1)' }} />
          <div style={{ position:'absolute', fontSize:56, right:14, top:'50%', transform:'translateY(-50%)', opacity:0.25 }}>📦</div>
          <p style={{ color:'rgba(255,255,255,0.75)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:6 }}>Chosen Package</p>
          <p style={{ color:'#fff', fontWeight:800, fontSize:15, marginBottom:4 }}>{profile?.package || 'Not assigned'}</p>
          <p style={{ color:'rgba(255,255,255,0.7)', fontSize:11, fontWeight:700, marginBottom:6 }}>Chosen Subjects:</p>
          <p style={{ color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:600, lineHeight:1.7 }}>{subjects.length ? subjects.join(', ') : 'None assigned'}</p>
        </div>

        {/* Announcements & Exam Details */}
        <div className="s-card-hover" style={{ ...card({ background:'linear-gradient(135deg,#7c3aed,#a855f7)', border:'none', padding:'24px', overflow:'hidden', position:'relative', cursor:'pointer' }), animation:'fadeUp 0.35s ease 0.08s both' }} onClick={() => { setAcademicInitTab('progress'); setActive('academic'); }}>
          <div style={{ position:'absolute', top:-15, right:-15, width:90, height:90, borderRadius:'50%', background:'rgba(255,255,255,0.1)' }} />
          <div style={{ position:'absolute', fontSize:52, right:14, top:'50%', transform:'translateY(-50%)', opacity:0.25 }}>📢</div>
          <p style={{ color:'rgba(255,255,255,0.75)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:8 }}>Exams & Results</p>
          {tests.length === 0 && scheduledTests.length === 0
            ? <p style={{ color:'rgba(255,255,255,0.8)', fontSize:13, fontWeight:600 }}>No exams yet</p>
            : <>
              {(upcomingTests.length > 0 || extraScheduledTests.length > 0) && (
                <>
                  <p style={{ color:'rgba(255,255,255,0.6)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:5 }}>📅 Upcoming</p>
                  {upcomingTests.map(t => (
                    <div key={t.grade_id} style={{ marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:'#fde68a', display:'inline-block', flexShrink:0 }} />
                      <span style={{ color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:600 }}>{t.test_name} · {fmtDate(t.test_date)}</span>
                    </div>
                  ))}
                  {extraScheduledTests.map(t => (
                    <div key={t.test_id} style={{ marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:'#fde68a', display:'inline-block', flexShrink:0 }} />
                      <span style={{ color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:600 }}>{t.test_name} · {fmtDate(t.test_date)}</span>
                    </div>
                  ))}
                </>
              )}
              {tests.filter(t => t.marks_obtained !== null).slice(0,2).length > 0 && (
                <>
                  <p style={{ color:'rgba(255,255,255,0.6)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:5, marginTop:upcomingTests.length?8:0 }}>🏆 Recent Results</p>
                  {tests.filter(t => t.marks_obtained !== null).slice(0,2).map(t => {
                    const pct = Math.round((t.marks_obtained / t.max_marks) * 100);
                    return (
                      <div key={t.grade_id} style={{ marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background: pct >= 40 ? '#86efac' : '#fca5a5', display:'inline-block', flexShrink:0 }} />
                        <span style={{ color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:600 }}>{t.test_name}</span>
                        <span style={{ marginLeft:'auto', fontSize:11, fontWeight:800, color: pct >= 40 ? '#86efac' : '#fca5a5' }}>{t.marks_obtained}/{t.max_marks}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          }
        </div>

        {/* Hours / Payment */}
        <div className="s-card-hover" style={{ ...card({ background: myHours?.exhausted ? 'linear-gradient(135deg,#dc2626,#f87171)' : 'linear-gradient(135deg,#2554b8,#60a5fa)', border:'none', padding:'24px', overflow:'hidden', position:'relative', cursor:'default' }), animation:'fadeUp 0.35s ease 0.16s both' }}>
          <div style={{ position:'absolute', top:-15, right:-15, width:90, height:90, borderRadius:'50%', background:'rgba(255,255,255,0.1)' }} />
          <div style={{ position:'absolute', fontSize:52, right:14, top:'50%', transform:'translateY(-50%)', opacity:0.25 }}>⏱️</div>
          <p style={{ color:'rgba(255,255,255,0.75)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:8 }}>
            {myHours?.billing_type === 'monthly' ? `Class Hours — ${myHours.month_label}` : 'Paid Class Hours'}
          </p>
          {myHours?.billing_type === 'monthly' ? (
            <>
              <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:8 }}>
                <span style={{ color:'#fff', fontWeight:900, fontSize:28 }}>{myHours.this_month_hours}</span>
                <span style={{ color:'rgba(255,255,255,0.7)', fontSize:14, fontWeight:600 }}>hrs used this month</span>
              </div>
              <p style={{ color:'rgba(255,255,255,0.85)', fontSize:12, fontWeight:700 }}>
                {myHours.pending_hours > 0 ? `💳 Payment pending for ${myHours.pending_hours}h — see Payment Information` : 'You are billed at the end of the month for the hours used.'}
              </p>
            </>
          ) : myHours && myHours.hours_per_month > 0 ? (
            <>
              <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:10 }}>
                <span style={{ color:'#fff', fontWeight:900, fontSize:28 }}>{myHours.hours_used}</span>
                <span style={{ color:'rgba(255,255,255,0.7)', fontSize:14, fontWeight:600 }}>/ {myHours.hours_per_month} hrs</span>
              </div>
              {/* Progress bar */}
              <div style={{ background:'rgba(255,255,255,0.25)', borderRadius:20, height:8, overflow:'hidden', marginBottom:8 }}>
                <div style={{ width:`${Math.min(100, myHours.pct)}%`, height:'100%', background:'#fff', borderRadius:20, transition:'width 0.6s' }} />
              </div>
              <p style={{ color:'rgba(255,255,255,0.85)', fontSize:12, fontWeight:700 }}>
                {myHours.exhausted
                  ? '⚠️ All paid hours used — your next bill will be shared soon'
                  : `${(myHours.hours_per_month - myHours.hours_used).toFixed(1)}h remaining${myHours.cycle_start ? ` · since ${myHours.cycle_start}` : ''}`}
              </p>
              <p style={{ color:'rgba(255,255,255,0.6)', fontSize:10.5, marginTop:6 }}>
                {myHours.pending_hours > 0 ? `💳 Payment pending for ${myHours.pending_hours}h — see Payment Information.` : 'Each class is counted against the hours you have paid for.'}
              </p>
            </>
          ) : (
            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:13, fontWeight:600, marginBottom:6 }}>Package: {profile?.pricing_plan || 'N/A'}</p>
          )}
        </div>
      </div>

      {/* Upcoming Schedule */}
      <div style={{ ...card({ padding:0, overflow:'hidden' }) }}>
        <div style={{ padding:'20px 26px', borderBottom:'1px solid #eef2fa', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h3 className="s-gradient-text" style={{ fontSize:18, fontWeight:800 }}>Upcoming Schedule</h3>
          <button className="s-btn" onClick={() => setActive('schedule')} style={{ padding:'7px 18px', borderRadius:10, border:'1.5px solid #2554b8', background:'transparent', color:'#2554b8', fontWeight:700, cursor:'pointer', fontSize:12 }}>View Calendar →</button>
        </div>
        {upcoming.length === 0 ? (
          <div style={{ textAlign:'center', padding:'52px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:64, marginBottom:16 }} className="floating">🌴</div>
            <p style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>You have no upcoming schedules.</p>
            <p style={{ fontSize:13 }}>Enjoy your free time!</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:460 }}>
            <thead>
              <tr style={{ background:'#f8faff' }}>
                {['Subject','Companion','Date','Time','Status'].map(h => (
                  <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {upcoming.map((s,i) => (
                <tr key={s.id} className="s-tr s-row" style={{ borderTop:'1px solid #eef2fa', animationDelay:`${i*0.06}s` }}>
                  <td style={{ padding:'13px 20px', fontWeight:700, color:'#1e293b', whiteSpace:'nowrap' }}>{s.subject}</td>
                  <td style={{ padding:'13px 20px', color:'#475569', whiteSpace:'nowrap' }}>{s.tutor_name||'—'}</td>
                  <td style={{ padding:'13px 20px', color:'#475569', fontFamily:'monospace', fontSize:13, whiteSpace:'nowrap' }}>{fmtDate(s.date)}</td>
                  <td style={{ padding:'13px 20px', color:'#475569', fontFamily:'monospace', fontSize:13, whiteSpace:'nowrap' }}>{fmtTime(s.start_time)}</td>
                  <td style={{ padding:'13px 20px' }}>
                    <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background:'rgba(37,84,184,0.1)', color:'#2554b8', border:'1px solid rgba(37,84,184,0.2)' }}>Scheduled</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PROFILE
═══════════════════════════════════════════════════ */
function Profile() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {}); }, []);
  if (!profile) return <LoadingState />;

  const fields = [
    ['Current Class', profile.class_name||'N/A'],
    ['Syllabus', profile.syllabus||'N/A'],
    ['Selected Package', profile.package||'N/A'],
    ['Selected Pricing Plan', profile.pricing_plan||'N/A'],
  ];

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      {/* Header */}
      <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:22 }) }}>
        <div style={{ background:'linear-gradient(135deg,#1a3c8f,#3b82f6)', padding:'32px 28px 24px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:20 }}>
            <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(255,255,255,0.2)', border:'4px solid rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:34, fontWeight:800, color:'#fff', backdropFilter:'blur(8px)', flexShrink:0 }}>
              {profile.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                <h2 style={{ color:'#fff', fontWeight:800, fontSize:22 }}>{profile.name}</h2>
                {profile.uid && <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:12, fontWeight:700, padding:'3px 14px', borderRadius:20, border:'1px solid rgba(255,255,255,0.3)' }}>{profile.uid}</span>}
              </div>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13 }}>{profile.email}</p>
              <span style={{ display:'inline-block', marginTop:6, background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:11.5, fontWeight:700, padding:'4px 14px', borderRadius:20, border:'1px solid rgba(255,255,255,0.25)' }}>🎓 Student</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Contact Details */}
        <div style={card()}>
          <h4 style={{ fontSize:13, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:16 }}>Contact Details</h4>
          {[['Alternate Email', profile.alt_email||'Not provided'],['Address', profile.address||'Not provided'],['Contact Number', profile.phone||'Not provided']].map(([k,v]) => (
            <div key={k} style={{ marginBottom:12 }}>
              <p style={{ fontSize:11, fontWeight:700, color:'#2554b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{k}</p>
              <div style={{ background:'#f0f5ff', borderRadius:10, padding:'11px 14px', fontSize:13, fontWeight:600, color:'#1e293b', border:'1px solid #dbeafe' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Academic & Package */}
        <div style={card()}>
          <h4 style={{ fontSize:13, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:16 }}>Academic and Package Details</h4>
          {fields.map(([k,v]) => (
            <div key={k} style={{ marginBottom:12 }}>
              <p style={{ fontSize:11, fontWeight:700, color:'#2554b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{k}</p>
              <div style={{ background:'#f0f5ff', borderRadius:10, padding:'11px 14px', fontSize:13, fontWeight:600, color:'#1e293b', border:'1px solid #dbeafe' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop:20, padding:'13px 18px', background:'rgba(220,38,38,0.06)', border:'1px solid rgba(220,38,38,0.18)', borderRadius:12, fontSize:13, color:'#b91c1c', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
        ⚠️ You cannot edit your profile. Please contact the admin for any changes.
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   ENROLLMENT DETAILS
═══════════════════════════════════════════════════ */
function EnrollmentDetails() {
  const [enrollments, setEnrollments] = useState([]);
  const [filter, setFilter] = useState('all');
  useEffect(() => { API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {}); }, []);
  const filtered = filter === 'all' ? enrollments : enrollments.filter(e => e.status === filter);
  const colors = ['#2554b8','#7c3aed','#16a34a','#d97706','#dc2626','#0891b2'];

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      <div style={{ marginBottom:20, display:'flex', gap:8 }}>
        {['all','active','completed','paused'].map(f => (
          <button key={f} style={pill(filter===f)} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState icon="🎓" text="No enrollments found" /> : (
        <>
          <h3 style={{ fontWeight:800, fontSize:18, color:'#0f1f45', marginBottom:20 }}>Assigned Companions</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:20 }}>
            {filtered.map((e,i) => (
              <div key={e.id} className="s-companion s-row"
                style={{ background:'#fff', borderRadius:22, border:'1px solid #e8edf8', overflow:'hidden', boxShadow:'0 4px 18px rgba(37,84,184,0.08)', animationDelay:`${i*0.08}s`, position:'relative' }}>
                {/* Status badge */}
                <div style={{ position:'absolute', top:14, left:14, padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:800, background:e.status==='active'?'#16a34a':'#2554b8', color:'#fff', textTransform:'capitalize', boxShadow:'0 2px 8px rgba(0,0,0,0.15)' }}>{e.status}</div>
                {/* Avatar area */}
                <div style={{ background:'linear-gradient(135deg,#f0f5ff,#dbeafe)', padding:'36px 20px 20px', textAlign:'center' }}>
                  <div style={{ width:72, height:72, borderRadius:'50%', background:`linear-gradient(135deg,${colors[i%colors.length]},${colors[(i+2)%colors.length]})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, margin:'0 auto 12px', boxShadow:`0 6px 20px ${colors[i%colors.length]}44` }}>
                    {(e.tutor_name||'T')[0].toUpperCase()}
                  </div>
                  <h4 style={{ fontWeight:800, fontSize:16, color:'#0f1f45', marginBottom:3 }}>{e.tutor_name||`Tutor #${e.tutor_id}`}</h4>
                  <p style={{ fontSize:12, color:'#64748b' }}>🎓 Companion</p>
                </div>
                {/* Info */}
                <div style={{ background:'rgba(240,245,255,0.7)', padding:'14px 18px', borderTop:'1px solid #e8edf8' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:700, color:'#1e293b', marginBottom:5 }}>📚 {e.subject}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748b' }}>📅 Started: {fmtDate(e.start_date)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Table view */}
          <div style={{ ...card({ padding:0, overflow:'hidden', marginTop:28 }) }}>
            <div style={{ padding:'16px 22px', borderBottom:'1px solid #f1f5f9' }}><h4 style={{ fontWeight:800, fontSize:15, color:'#0f1f45' }}>Enrollment Summary</h4></div>
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:480 }}>
              <thead><tr style={{ background:'#f8faff' }}>
                {['Subject','Companion','Status','Start Date','End Date'].map(h => (
                  <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map((e,i) => (
                  <tr key={e.id} className="s-tr s-row" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.06}s` }}>
                    <td style={{ padding:'12px 18px', fontWeight:700, color:'#0f1f45', whiteSpace:'nowrap' }}>{e.subject}</td>
                    <td style={{ padding:'12px 18px', color:'#475569', whiteSpace:'nowrap' }}>{e.tutor_name||`#${e.tutor_id}`}</td>
                    <td style={{ padding:'12px 18px' }}>
                      <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background:e.status==='active'?'rgba(22,163,74,0.1)':'rgba(37,84,184,0.1)', color:e.status==='active'?'#16a34a':'#2554b8', border:`1px solid ${e.status==='active'?'rgba(22,163,74,0.25)':'rgba(37,84,184,0.25)'}` }}>{e.status}</span>
                    </td>
                    <td style={{ padding:'12px 18px', fontFamily:'monospace', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtDate(e.start_date)}</td>
                    <td style={{ padding:'12px 18px', fontFamily:'monospace', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtDate(e.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PAYMENT INFORMATION
═══════════════════════════════════════════════════ */
function PaymentInformation() {
  const [profile, setProfile] = useState(null);
  const [myHours, setMyHours] = useState(null);
  const [payments, setPayments] = useState([]);
  const [myBills, setMyBills] = useState(null);

  useEffect(() => {
    API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {});
    API.get('/payments/my-hours').then(r => setMyHours(r.data)).catch(() => {});
    API.get('/payments/student-payments/my').then(r => setPayments(r.data || [])).catch(() => {});
    API.get('/bills/my').then(r => setMyBills(r.data)).catch(() => {});
  }, []);

  const balanceHours = myHours ? Math.max(0, (myHours.hours_per_month || 0) - (myHours.hours_used || 0)) : null;
  const pct = myHours?.pct || 0;
  const barColor = pct >= 100 ? '#dc2626' : pct >= 75 ? '#f59e0b' : '#16a34a';

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      <div style={{ maxWidth:760, margin:'0 auto' }}>

        {/* Hours card */}
        {myHours?.billing_type === 'monthly' ? (
          <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:20 }) }}>
            <div style={{ background:'linear-gradient(135deg,#1a3c8f,#2554b8)', padding:'22px 28px' }}>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Hours used — {myHours.month_label}</p>
              <div style={{ color:'#fff', fontWeight:800, fontSize:32, lineHeight:1 }}>{myHours.this_month_hours}h</div>
              <div style={{ marginTop:10, padding:'6px 12px', borderRadius:8, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.85)', fontSize:11 }}>
                ℹ️ You are billed after each month for the hours actually used.
              </div>
            </div>
          </div>
        ) : myHours?.hours_per_month > 0 && (
          <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:20 }) }}>
            <div style={{ background:'linear-gradient(135deg,#1a3c8f,#2554b8)', padding:'22px 28px' }}>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Paid Hours</p>
              <div style={{ display:'flex', alignItems:'flex-end', gap:16, flexWrap:'wrap' }}>
                <div>
                  <div style={{ color:'#fff', fontWeight:800, fontSize:32, lineHeight:1 }}>{balanceHours}h</div>
                  <div style={{ color:'rgba(255,255,255,0.65)', fontSize:12, marginTop:3 }}>remaining of {myHours.hours_per_month}h</div>
                </div>
                <div style={{ flex:1, minWidth:160 }}>
                  <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:20, height:10, overflow:'hidden', marginBottom:6 }}>
                    <div style={{ width:`${Math.min(100, pct)}%`, height:'100%', background: pct >= 100 ? '#fca5a5' : '#93c5fd', borderRadius:20, transition:'width 0.5s' }} />
                  </div>
                  <div style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>
                    {myHours.hours_used}h used{myHours.cycle_start ? ` · since ${myHours.cycle_start}` : ''}
                  </div>
                </div>
              </div>
              {myHours.exhausted && (
                <div style={{ marginTop:12, padding:'8px 14px', borderRadius:8, background:'rgba(239,68,68,0.25)', color:'#fecaca', fontSize:12, fontWeight:700 }}>
                  ⚠️ All paid hours used — your next bill will be shared soon
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bills: what was billed, what's paid, what's pending */}
        {myBills && myBills.bills.length > 0 && (
          <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:20 }) }}>
            <div style={{ padding:'16px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
              <h4 style={{ fontWeight:800, color:'#0f1f45' }}>My Bills</h4>
              {myBills.pending_hours > 0
                ? <span style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:20, background:'#fef3c7', color:'#92400e' }}>💳 Payment pending for {myBills.pending_hours}h</span>
                : <span style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:20, background:'#dcfce7', color:'#15803d' }}>✓ All bills paid</span>}
            </div>
            {myBills.bills.map(b => {
              const st = b.pay_status === 'paid' ? { bg:'#dcfce7', fg:'#15803d', t:'Paid' }
                : b.pay_status === 'partial' ? { bg:'#fef3c7', fg:'#92400e', t:`Partly paid · ${b.pending_hours}h pending` }
                : { bg:'#fee2e2', fg:'#b91c1c', t:'Payment pending' };
              return (
                <div key={b.id} style={{ padding:'14px 24px', borderTop:'1px solid #f1f5f9' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
                    <div>
                      <div style={{ fontWeight:800, color:'#0f1f45', fontSize:14 }}>{b.label} <span style={{ color:'#2554b8' }}>· {b.hours}h</span></div>
                      <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
                        {b.billing_type === 'monthly' ? `${b.period_start} to ${b.period_end}` : `From ${b.period_start}`}{b.invoice_number ? ` · Invoice ${b.invoice_number}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:st.bg, color:st.fg, whiteSpace:'nowrap' }}>{st.t}</span>
                  </div>
                  {b.payments.length > 0 && (
                    <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:3 }}>
                      {b.payments.map(p => (
                        <div key={p.id} style={{ fontSize:12.5, color:'#334155' }}>
                          ✓ Paid <b style={{ color:'#16a34a' }}>₹{Number(p.amount).toLocaleString('en-IN')}</b> on {p.payment_date}{p.hours != null ? ` for ${p.hours}h` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Plan card */}
        <div style={{ ...card({ padding:0, overflow:'hidden', marginBottom:20 }) }}>
          <div style={{ background:'linear-gradient(135deg,#2554b8,#3b82f6)', padding:'24px 28px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <p style={{ color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Current Plan</p>
                <h3 style={{ color:'#fff', fontWeight:800, fontSize:20, marginBottom:4 }}>{profile?.package||'Not assigned'}</h3>
                <p style={{ color:'rgba(255,255,255,0.75)', fontSize:13 }}>{profile?.pricing_plan||'N/A'}</p>
              </div>
              <div style={{ fontSize:60, opacity:0.3 }}>💳</div>
            </div>
          </div>
          <div style={{ padding:'20px 28px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[['Package',profile?.package||'N/A'],['Pricing Plan',profile?.pricing_plan||'N/A'],['Class',profile?.class_name||'N/A'],['Mode',profile?.mode||'N/A']].map(([k,v]) => (
                <div key={k} style={{ background:'#f8faff', borderRadius:12, padding:'12px 16px', border:'1px solid #eef2fa' }}>
                  <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:3 }}>{k}</p>
                  <p style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment history */}
        <div style={card({ padding:0, overflow:'hidden' })}>
          <div style={{ padding:'16px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <h4 style={{ fontWeight:800, color:'#0f1f45' }}>Payment History</h4>
            <span style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>{payments.length} record{payments.length !== 1 ? 's' : ''}</span>
          </div>
          {payments.length === 0 ? (
            <div style={{ textAlign:'center', padding:'44px 20px', color:'#94a3b8' }}>
              <div style={{ fontSize:48, marginBottom:10, opacity:0.5 }}>💸</div>
              <p style={{ fontWeight:700, fontSize:14 }}>No payments recorded yet</p>
              <p style={{ fontSize:13, marginTop:4 }}>Your payment history will appear here once processed by admin.</p>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f8faff' }}>
                  {['Invoice #','Amount','Payment Date','Notes'].map(h => (
                    <th key={h} style={{ padding:'11px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'13px 20px', fontFamily:'monospace', fontSize:13, color:'#2554b8', fontWeight:700 }}>{p.invoice_number || '—'}</td>
                    <td style={{ padding:'13px 20px', fontWeight:800, fontSize:15, color:'#16a34a' }}>₹{Number(p.amount).toLocaleString()}</td>
                    <td style={{ padding:'13px 20px', fontSize:13, color:'#475569' }}>{p.payment_date}</td>
                    <td style={{ padding:'13px 20px', fontSize:13, color:'#64748b', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   ACADEMIC DETAILS
═══════════════════════════════════════════════════ */
function AcademicDetails({ initTab = 'attendance', examSessions = [], onStartExam }) {
  const [logs, setLogs] = useState([]);
  const [myGrades, setMyGrades] = useState([]);
  const [profile, setProfile] = useState(null);
  const [myHours, setMyHours] = useState(null);
  const [tutorFeedback, setTutorFeedback] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfStart, setPdfStart] = useState('');
  const [pdfEnd, setPdfEnd] = useState('');
  const [tab, setTab] = useState(initTab);
  const [copiedCode, setCopiedCode] = useState(null);
  useEffect(() => {
    API.get('/class/logs').then(r => setLogs(r.data)).catch(() => {});
    API.get('/tests/my-grades').then(r => setMyGrades(r.data)).catch(() => {});
    API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {});
    API.get('/payments/my-hours').then(r => setMyHours(r.data)).catch(() => {});
    API.get('/tutor-feedback/mine').then(r => setTutorFeedback(r.data || [])).catch(() => {});
  }, []);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      // Filter logs and grades by selected date range
      const filteredLogs = pdfStart || pdfEnd
        ? logs.filter(l => {
            const d = l.updated_at?.slice(0, 10) || '';
            return (!pdfStart || d >= pdfStart) && (!pdfEnd || d <= pdfEnd);
          })
        : logs;
      const filteredGrades = pdfStart || pdfEnd
        ? myGrades.filter(g => {
            const d = g.test_date || '';
            return (!pdfStart || d >= pdfStart) && (!pdfEnd || d <= pdfEnd);
          })
        : myGrades;
      const filteredFeedback = pdfStart || pdfEnd
        ? tutorFeedback.filter(f => (!pdfStart || f.month >= pdfStart.slice(0,7)) && (!pdfEnd || f.month <= pdfEnd.slice(0,7)))
        : tutorFeedback;
      await generateStudentPDF({ profile, logs: filteredLogs, grades: filteredGrades, hours: myHours, feedback: filteredFeedback, dateRange: pdfStart || pdfEnd ? `${pdfStart || 'start'} to ${pdfEnd || 'today'}` : null });
    } catch (e) { console.error(e); }
    setPdfLoading(false);
  };

  const completed = logs.filter(l => l.status==='completed').length;
  const cancelled = logs.filter(l => l.status==='cancelled').length;
  const attendancePct = logs.length ? Math.round((completed/logs.length)*100) : 0;

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      {/* Page header with download button */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:14 }}>
        <div>
          <h2 style={{ fontWeight:800, color:'#0f1f45', fontSize:22, marginBottom:4 }}>Academic Details</h2>
          <p style={{ color:'#64748b', fontSize:13 }}>Your class attendance, progress, and test results</p>
        </div>
        <div style={{ display:'flex', alignItems:'flex-end', gap:10, flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5, display:'block', marginBottom:4 }}>From</label>
            <input type="date" value={pdfStart} onChange={e => setPdfStart(e.target.value)}
              style={{ padding:'7px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:12, fontWeight:600 }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5, display:'block', marginBottom:4 }}>To</label>
            <input type="date" value={pdfEnd} onChange={e => setPdfEnd(e.target.value)}
              style={{ padding:'7px 10px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:12, fontWeight:600 }} />
          </div>
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            style={{ display:'flex', alignItems:'center', gap:8, background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', border:'none', borderRadius:12, padding:'11px 22px', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(37,84,184,0.35)', opacity: pdfLoading ? 0.7 : 1 }}
          >
            {pdfLoading ? '⏳ Generating…' : '📥 Download Report'}
          </button>
        </div>
      </div>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:16, marginBottom:24 }}>
        {[
          { label:'Total Classes', value:logs.length, icon:'📅', color:'#2554b8', bg:'#eef3ff' },
          { label:'Attended', value:completed, icon:'✅', color:'#16a34a', bg:'#f0fdf4' },
          { label:'Cancelled', value:cancelled, icon:'❌', color:'#dc2626', bg:'#fef2f2' },
          { label:'Attendance %', value:`${attendancePct}%`, icon:'📊', color:'#7c3aed', bg:'#f5f3ff' },
        ].map((s,i) => (
          <div key={s.label} className="s-stat" style={{ background:'#fff', borderRadius:18, padding:'20px 22px', border:'1px solid #e8edf8', boxShadow:'0 4px 16px rgba(37,84,184,0.07)', animation:`fadeUp 0.35s ease ${i*0.07}s both` }}>
            <div style={{ width:48, height:48, borderRadius:14, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, marginBottom:14 }}>{s.icon}</div>
            <div style={{ fontSize:28, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
            <div style={{ fontSize:11.5, color:'#64748b', fontWeight:600, marginTop:5, textTransform:'uppercase', letterSpacing:0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:0, marginBottom:22, background:'#f0f4fb', borderRadius:14, padding:5, maxWidth:'100%', overflowX:'auto', WebkitOverflowScrolling:'touch', border:'1px solid #e2e8f6', flexWrap:'nowrap' }}>
        {[['attendance','📋 Attendance'],['progress','🏆 Progress Evaluation']].map(([t,label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:'9px 24px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:13, background:tab===t?'#fff':'transparent', color:tab===t?'#2554b8':'#64748b', boxShadow:tab===t?'0 2px 10px rgba(37,84,184,0.12)':'none', transition:'all 0.2s', whiteSpace:'nowrap', flexShrink:0 }}>{label}</button>
        ))}
      </div>

      {tab === 'attendance' && (
        <div style={card({ padding:0, overflow:'hidden' })}>
          {logs.length === 0 ? <EmptyState icon="📋" text="No attendance records yet" /> : (
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:560 }}>
              <thead><tr style={{ background:'#f8faff' }}>
                {['Subject','Companion','Status','Topics Covered','Homework','Date'].map(h => (
                  <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {logs.map((l,i) => (
                  <tr key={l.id} className="s-tr s-row" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.05}s` }}>
                    <td style={{ padding:'12px 18px', fontWeight:700, color:'#0f1f45', whiteSpace:'nowrap' }}>{l.subject||'—'}</td>
                    <td style={{ padding:'12px 18px', color:'#475569', whiteSpace:'nowrap' }}>{l.tutor_name||'—'}</td>
                    <td style={{ padding:'12px 18px' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:3, alignItems:'flex-start' }}>
                        <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background:l.status==='completed'?'rgba(22,163,74,0.1)':'rgba(220,38,38,0.1)', color:l.status==='completed'?'#16a34a':'#dc2626', border:`1px solid ${l.status==='completed'?'rgba(22,163,74,0.2)':'rgba(220,38,38,0.2)'}` }}>{l.status}</span>
                        {l.marked_by_admin && (
                          <span style={{ fontSize:9.5, fontWeight:700, padding:'1px 7px', borderRadius:10, background:'rgba(124,58,237,0.08)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.2)' }}>Marked by Admin</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding:'12px 18px', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#475569', fontSize:13 }}>{l.topics||'—'}</td>
                    <td style={{ padding:'12px 18px', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#475569', fontSize:13 }}>{l.homework||'—'}</td>
                    <td style={{ padding:'12px 18px', fontFamily:'monospace', fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{l.updated_at?.slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {tab === 'progress' && (
        <div>
          {/* Companion Feedback */}
          {tutorFeedback.length > 0 && (
            <div style={{ marginBottom:24 }}>
              <h4 style={{ fontWeight:800, color:'#0f1f45', marginBottom:14, fontSize:15 }}>💬 Companion Feedback</h4>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {tutorFeedback.map(f => (
                  <div key={f.id} style={{ background:'#fff', borderRadius:14, padding:'16px 20px', border:'1px solid #e2e8f0', boxShadow:'0 2px 8px rgba(37,84,184,0.05)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:10 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <span style={{ fontSize:14, fontWeight:800, color:'#0f1f45' }}>{f.subject}</span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background:'#eff6ff', color:'#2554b8' }}>{f.month}</span>
                      </div>
                      <span style={{ fontSize:12, color:'#64748b' }}>by {f.tutor_name}</span>
                    </div>
                    {f.progress_summary && <div style={{ fontSize:13, color:'#475569', marginBottom:8, lineHeight:1.6 }}><b style={{ color:'#16a34a' }}>📈 Progress:</b> {f.progress_summary}</div>}
                    {f.improvements && <div style={{ fontSize:13, color:'#475569', marginBottom:8, lineHeight:1.6 }}><b style={{ color:'#2554b8' }}>✨ Improvements:</b> {f.improvements}</div>}
                    {f.focus_areas && <div style={{ fontSize:13, color:'#475569', marginBottom:8, lineHeight:1.6 }}><b style={{ color:'#dc2626' }}>🎯 Focus Areas:</b> {f.focus_areas}</div>}
                    {f.chapters_completed && <div style={{ fontSize:13, color:'#475569', lineHeight:1.6 }}><b style={{ color:'#7c3aed' }}>📚 Chapters:</b> {f.chapters_completed}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {myGrades.length === 0 ? (
            <div style={card({ textAlign:'center', padding:52 })}>
              <div style={{ fontSize:72, marginBottom:16, opacity:0.8 }} className="floating">📈</div>
              <h3 style={{ fontWeight:800, fontSize:20, color:'#0f1f45', marginBottom:8 }}>No Tests Yet</h3>
              <p style={{ color:'#64748b', maxWidth:420, margin:'0 auto', lineHeight:1.75, fontSize:14 }}>Your Companion has not assigned any tests yet. Check back soon!</p>
            </div>
          ) : (
            <>
              {/* Upcoming Exams — newest first; completed online exams hidden */}
              {(() => {
                const upcoming = myGrades
                  .filter(g => g.marks_obtained === null)
                  .filter(g => {
                    // Offline tests (no exam code) stay until the tutor grades them.
                    if (!g.exam_secret_code) return true;
                    // Online exams: hide once the student has submitted (completed).
                    const session = examSessions.find(s => s.secret_code?.toUpperCase() === g.exam_secret_code?.toUpperCase());
                    return !(session && session.completed_at);
                  })
                  // Newest exam on top.
                  .sort((a, b) => (b.test_date || '').localeCompare(a.test_date || ''));
                if (upcoming.length === 0) return null;
                return (
                <div style={{ marginBottom:24 }}>
                  <h4 style={{ fontWeight:800, color:'#0f1f45', marginBottom:14, fontSize:15 }}>📝 Upcoming Exams</h4>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:14 }}>
                    {upcoming.map((g,i) => (
                      <div key={g.grade_id} style={{ background:'linear-gradient(135deg,#f0f5ff,#e8eeff)', borderRadius:14, padding:'16px 18px', border:'1px solid #dbeafe', animation:`fadeUp 0.3s ease ${i*0.06}s both` }}>
                        <p style={{ fontWeight:800, fontSize:14, color:'#1e293b', marginBottom:5 }}>{g.test_name}</p>
                        <p style={{ fontSize:12, color:'#2554b8', fontWeight:600, marginBottom:3 }}>📚 {g.subject}</p>
                        {g.topic && <p style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>📌 {g.topic}</p>}
                        <p style={{ fontSize:12, color:'#64748b' }}>📅 {fmtDate(g.test_date)}</p>
                        <p style={{ fontSize:12, color:'#7c3aed', fontWeight:700, marginTop:6 }}>Max: {g.max_marks} marks</p>
                        {g.exam_secret_code && (() => {
                          const session = examSessions.find(s => s.secret_code?.toUpperCase() === g.exam_secret_code?.toUpperCase());
                          const isCompleted = session && Boolean(session.completed_at);
                          const isInProgress = session && !session.completed_at;
                          return (
                            <div style={{ marginTop:8 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:6 }}>
                                <div style={{ padding:'4px 10px', background:'rgba(124,58,237,0.1)', borderRadius:8, border:'1px solid rgba(124,58,237,0.2)', display:'inline-flex', alignItems:'center', gap:6 }}>
                                  <span style={{ fontSize:10, fontWeight:700, color:'#7c3aed' }}>EXAM CODE: </span>
                                  <span style={{ fontSize:12, fontWeight:800, color:'#7c3aed', letterSpacing:1.5 }}>{g.exam_secret_code}</span>
                                </div>
                                <button onClick={() => { navigator.clipboard.writeText(g.exam_secret_code); setCopiedCode(g.grade_id); setTimeout(() => setCopiedCode(null), 2000); }}
                                  style={{ padding:'4px 10px', borderRadius:8, border:'1px solid rgba(124,58,237,0.3)', background: copiedCode===g.grade_id ? 'rgba(22,163,74,0.1)' : 'rgba(124,58,237,0.08)', color: copiedCode===g.grade_id ? '#16a34a' : '#7c3aed', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                                  {copiedCode===g.grade_id ? '✓ Copied' : '📋 Copy'}
                                </button>
                              </div>
                              {isCompleted ? (
                                <span style={{ display:'inline-block', padding:'3px 12px', borderRadius:20, background:'rgba(22,163,74,0.1)', color:'#16a34a', fontSize:11, fontWeight:700, border:'1px solid rgba(22,163,74,0.3)' }}>✓ Completed</span>
                              ) : (
                                <button
                                  onClick={() => onStartExam && onStartExam(g.exam_secret_code)}
                                  style={{ display:'block', width:'100%', padding:'10px 14px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7c3aed,#a855f7)', color:'#fff', fontWeight:800, fontSize:13, cursor:'pointer', boxShadow:'0 4px 12px rgba(124,58,237,0.30)', letterSpacing:0.3 }}>
                                  {isInProgress ? '▶ Resume Exam' : '✏️ Start Exam Now'}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })()}
              {/* Test Results */}
              {myGrades.filter(g => g.marks_obtained !== null).length > 0 && (
                <div>
                  <h4 style={{ fontWeight:800, color:'#0f1f45', marginBottom:14, fontSize:15 }}>🏆 Test Results</h4>
                  <div style={card({ padding:0, overflow:'hidden' })}>
                    <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:520 }}>
                      <thead><tr style={{ background:'#f8faff' }}>
                        {['Test Name','Subject','Date','Score','Remarks','Result'].map(h => (
                          <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {myGrades.filter(g => g.marks_obtained !== null)
                          .sort((a, b) => (b.test_date || '').localeCompare(a.test_date || ''))
                          .map((g,i) => {
                          const pct = Math.round((g.marks_obtained / g.max_marks) * 100);
                          const pass = pct >= 40;
                          return (
                            <tr key={g.grade_id} className="s-tr s-row" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.05}s` }}>
                              <td style={{ padding:'12px 18px', fontWeight:700, color:'#0f1f45', whiteSpace:'nowrap' }}>{g.test_name}</td>
                              <td style={{ padding:'12px 18px', color:'#475569', whiteSpace:'nowrap' }}>{g.subject}</td>
                              <td style={{ padding:'12px 18px', fontFamily:'monospace', fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{fmtDate(g.test_date)}</td>
                              <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                <span style={{ fontWeight:800, color:'#2554b8', fontSize:15 }}>{g.marks_obtained}</span>
                                <span style={{ color:'#94a3b8', fontSize:12 }}> / {g.max_marks}</span>
                                <span style={{ marginLeft:8, fontSize:11, color:'#64748b' }}>({pct}%)</span>
                              </td>
                              <td style={{ padding:'12px 18px', color:'#475569', fontSize:13, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.remarks || '—'}</td>
                              <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, background: pass ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)', color: pass ? '#16a34a' : '#dc2626', border:`1px solid ${pass ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}` }}>{pass ? 'Pass' : 'Fail'}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SCHEDULES (Calendar)
═══════════════════════════════════════════════════ */
const STUDENT_COLORS = { completed:'#16a34a', cancelled:'#dc2626', scheduled:'#2554b8' };

function CalEventStudent({ event }) {
  const icon = event._type === 'exam' ? '✏️' : event.cancel_reason === 'tutor_leave' ? '🏖️' : event.cancel_reason === 'student_leave' ? '📝' : { completed:'✓', cancelled:'✕', scheduled:'◉' }[event.status] || '◉';
  const label = event._type === 'exam' ? event._grade?.test_name || event.title : event.title;
  const sub = event._type === 'exam' ? event._grade?.subject : event.tutor_name ? `👤 ${event.tutor_name}` : null;
  return (
    <>
      <div style={{ fontWeight:700, fontSize:11, color:'#fff', lineHeight:'15px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {icon} {label}
      </div>
      {sub && (
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.88)', lineHeight:'13px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:1 }}>
          {sub}
        </div>
      )}
    </>
  );
}

function Schedules({ examSessions = [], onStartExam }) {
  const [schedules, setSchedules] = useState([]);
  const [myGrades, setMyGrades] = useState([]);
  const [detail, setDetail] = useState(null);
  const [profile, setProfile] = useState(null);
  const [calHeight, setCalHeight] = useState(window.innerWidth <= 768 ? 520 : 680);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [copiedExamCode, setCopiedExamCode] = useState(false);

  useEffect(() => {
    API.get('/schedule/').then(r => setSchedules(r.data)).catch(() => {});
    API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {});
    API.get('/tests/my-grades').then(r => setMyGrades(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setCalHeight(mobile ? 520 : 680);
      setIsMobile(mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const scheduleEvents = schedules.map(s => ({
    ...s,
    _type: 'class',
    title: s.cancel_reason === 'tutor_leave' ? `🏖️ ${s.subject} (Tutor Leave)` : s.cancel_reason === 'student_leave' ? `📝 ${s.subject} (Student Leave)` : s.subject || 'Class',
    start: new Date(`${s.date}T${s.start_time}+05:30`),
    end: new Date(`${s.date}T${s.end_time}+05:30`),
    resource: s,
  }));

  const examEvents = myGrades.map(g => {
    const d = new Date(g.test_date);
    const start = new Date(d); start.setHours(0, 0, 0);
    const end = new Date(d); end.setHours(1, 0, 0);
    return {
      _type: 'exam',
      _grade: g,
      title: `✏️ ${g.test_name}${g.marks_obtained !== null ? ` (${g.marks_obtained}/${g.max_marks})` : ''}`,
      start, end,
      resource: g,
    };
  });

  const events = [...scheduleEvents, ...examEvents];

  const leaveCancel = schedules.filter(s => s.cancel_reason === 'tutor_leave' && new Date(s.date) >= new Date(new Date().toDateString()));

  return (
    <div style={{ padding:'28px 32px', display:'flex', gap:22 }} className="s-page schedule-wrap">
      <div style={{ flex:1, minWidth:0 }}>
        {/* Tutor leave notice */}
        {leaveCancel.length > 0 && (
          <div style={{ borderRadius:14, padding:'14px 20px', marginBottom:16, background:'rgba(249,115,22,0.08)', border:'1.5px solid rgba(249,115,22,0.25)', display:'flex', gap:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:24, flexShrink:0 }}>🏖️</span>
            <div>
              <div style={{ fontWeight:800, color:'#c2410c', fontSize:14, marginBottom:4 }}>Classes Cancelled — Companion Leave</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {leaveCancel.map(s => (
                  <span key={s.id} style={{ background:'rgba(249,115,22,0.12)', border:'1px solid rgba(249,115,22,0.3)', color:'#c2410c', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20 }}>
                    {s.subject} · {fmtDate(s.date)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={card({ padding:20 })}>
          <h3 className="s-gradient-text" style={{ textAlign:'center', fontSize:18, fontWeight:800, marginBottom:8 }}>My Schedule</h3>
          <div style={{ display:'flex', gap:14, justifyContent:'center', marginBottom:14, flexWrap:'wrap' }}>
            {[['#2554b8','◉ Scheduled'],['#16a34a','✓ Completed'],['#dc2626','✕ Cancelled'],['#f97316','🏖️ Tutor Leave'],['#7c3aed','✏️ Exam'],['#dc2626','📝 Student Leave']].map(([c,l])=>(
              <span key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#64748b', fontWeight:600 }}>
                <span style={{ width:12, height:12, borderRadius:3, background:c, display:'inline-block', boxShadow:`0 2px 4px ${c}66` }} />{l}
              </span>
            ))}
          </div>
          <Calendar localizer={localizer} events={events} startAccessor="start" endAccessor="end" style={{ height:calHeight }}
            defaultView="month"
            views={['month','week','day','agenda']}
            components={{ event: CalEventStudent }}
            eventPropGetter={e => ({ className: e._type==='exam' ? 'rbc-evt-exam' : e.cancel_reason==='tutor_leave' ? 'rbc-evt-leave' : e.cancel_reason==='student_leave' ? 'rbc-evt-cancelled' : `rbc-evt-${e.status||'scheduled'}` })}
            onSelectEvent={e => setDetail(e)}
            popup />
        </div>
      </div>

      {detail && (
        <>
          {isMobile && (
            <div style={{ position:'fixed', inset:0, zIndex:1999, background:'rgba(0,0,0,0.45)' }} onClick={() => setDetail(null)} />
          )}
          <div className={isMobile ? '' : 'schedule-detail-panel'}
            style={isMobile
              ? { position:'fixed', bottom:0, left:0, right:0, zIndex:2000, borderRadius:'20px 20px 0 0', padding:24, background:'#fff', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 -8px 32px rgba(0,0,0,0.2)', animation:'fadeUp 0.25s ease' }
              : { width:310, flexShrink:0, animation:'fadeUp 0.25s ease' }}>
          <div style={isMobile ? {} : card({ position:'sticky', top:80, padding:24 })}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color: detail._type==='exam' ? '#7c3aed' : '#2554b8' }}>
                  {detail._type==='exam' ? '✏️ Exam Details' : 'Schedule Details'}
                </div>
                {detail._type !== 'exam' && (
                  <span style={{ display:'inline-block', marginTop:5, padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:700, background: detail.cancel_reason==='tutor_leave' ? 'rgba(249,115,22,0.12)' : detail.cancel_reason==='student_leave' ? 'rgba(220,38,38,0.12)' : `${STUDENT_COLORS[detail.status]}18`, color: detail.cancel_reason==='tutor_leave' ? '#c2410c' : detail.cancel_reason==='student_leave' ? '#dc2626' : STUDENT_COLORS[detail.status], border:`1px solid ${detail.cancel_reason==='tutor_leave' ? 'rgba(249,115,22,0.3)' : detail.cancel_reason==='student_leave' ? 'rgba(220,38,38,0.3)' : STUDENT_COLORS[detail.status]+'30'}` }}>
                    {detail.cancel_reason==='tutor_leave' ? '🏖️ Companion Leave' : detail.cancel_reason==='student_leave' ? '📝 Student Leave' : detail.status}
                  </span>
                )}
              </div>
              <button onClick={() => setDetail(null)} style={{ width:28, height:28, borderRadius:8, border:'none', background:'#f1f5f9', cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
            </div>

            {detail._type === 'exam' ? (
              /* Exam detail panel */
              <>
                {[['Test Name', detail._grade.test_name], ['Subject', detail._grade.subject], ['Topic', detail._grade.topic||'—'], ['Date', fmtDate(detail._grade.test_date)], ['Max Marks', detail._grade.max_marks]].map(([k,v]) => (
                  <div key={k} style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:0.8, marginBottom:3 }}>{k}</div>
                    <div style={{ fontWeight:700, color:'#0f1f45', fontSize:14 }}>{v}</div>
                  </div>
                ))}
                {detail._grade.exam_secret_code && (
                  <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ padding:'8px 14px', background:'rgba(124,58,237,0.08)', borderRadius:8, border:'1px solid rgba(124,58,237,0.2)', display:'inline-flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:'#7c3aed' }}>EXAM CODE: </span>
                      <span style={{ fontSize:14, fontWeight:800, color:'#7c3aed', letterSpacing:2 }}>{detail._grade.exam_secret_code}</span>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(detail._grade.exam_secret_code); setCopiedExamCode(true); setTimeout(() => setCopiedExamCode(false), 2000); }}
                      style={{ padding:'7px 12px', borderRadius:8, border:'1px solid rgba(124,58,237,0.3)', background: copiedExamCode ? 'rgba(22,163,74,0.1)' : 'rgba(124,58,237,0.08)', color: copiedExamCode ? '#16a34a' : '#7c3aed', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      {copiedExamCode ? '✓ Copied' : '📋 Copy'}
                    </button>
                  </div>
                )}
                {(() => {
                  const code = detail._grade.exam_secret_code;
                  if (!code) return null;
                  const session = examSessions.find(s => s.secret_code?.toUpperCase() === code?.toUpperCase());
                  if (session && session.completed_at) {
                    return <div style={{ marginTop:12, padding:'10px 14px', borderRadius:12, background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.3)', color:'#16a34a', fontWeight:700, fontSize:14 }}>✓ Exam Submitted</div>;
                  }
                  const isResumable = !!(session && !session.completed_at);
                  return (
                    <button
                      onClick={() => onStartExam && onStartExam(code)}
                      style={{ display:'block', width:'100%', marginTop:14, padding:'14px 16px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#7c3aed,#a855f7)', color:'#fff', fontWeight:800, fontSize:15, cursor:'pointer', boxShadow:'0 6px 20px rgba(124,58,237,0.35)', letterSpacing:0.3 }}>
                      {isResumable ? '▶ Resume Exam' : '✏️ Start Exam Now'}
                    </button>
                  );
                })()}
                {detail._grade.marks_obtained !== null ? (
                  <div style={{ borderRadius:12, padding:'14px 16px', background:'linear-gradient(135deg,#f5f3ff,#ede9fe)', border:'1px solid #ddd6fe', marginTop:4 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8 }}>Result</div>
                    <div style={{ fontSize:28, fontWeight:800, color:'#0f1f45' }}>{detail._grade.marks_obtained}<span style={{ fontSize:15, color:'#64748b', fontWeight:600 }}>/{detail._grade.max_marks}</span></div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#7c3aed', marginTop:4 }}>{Math.round((detail._grade.marks_obtained/detail._grade.max_marks)*100)}%</div>
                    <span style={{ display:'inline-block', marginTop:8, padding:'4px 14px', borderRadius:20, fontSize:12, fontWeight:700, background: Math.round((detail._grade.marks_obtained/detail._grade.max_marks)*100) >= 40 ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)', color: Math.round((detail._grade.marks_obtained/detail._grade.max_marks)*100) >= 40 ? '#16a34a' : '#dc2626', border:`1px solid ${Math.round((detail._grade.marks_obtained/detail._grade.max_marks)*100) >= 40 ? 'rgba(22,163,74,0.3)' : 'rgba(220,38,38,0.3)'}` }}>
                      {Math.round((detail._grade.marks_obtained/detail._grade.max_marks)*100) >= 40 ? '✓ Pass' : '✗ Fail'}
                    </span>
                    {detail._grade.remarks && <p style={{ fontSize:12, color:'#64748b', marginTop:8, fontStyle:'italic' }}>"{detail._grade.remarks}"</p>}
                  </div>
                ) : (
                  <div style={{ borderRadius:12, padding:'12px 16px', background:'rgba(217,119,6,0.08)', border:'1px solid rgba(217,119,6,0.25)', fontSize:13, color:'#d97706', fontWeight:600 }}>
                    Results not published yet. Check back soon!
                  </div>
                )}
              </>
            ) : (
              /* Class detail panel */
              <>
                {detail.cancel_reason === 'tutor_leave' && (
                  <div style={{ background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.25)', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#c2410c', fontWeight:600 }}>
                    This class was cancelled because your tutor is on leave.
                  </div>
                )}
                {detail.cancel_reason === 'student_leave' && (
                  <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.25)', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#dc2626', fontWeight:600 }}>
                    This class was cancelled due to your leave request.
                  </div>
                )}
                {[['Subject',detail.subject],['Companion',detail.tutor_name||'—'],['Date',fmtDate(detail.date)],['From',fmtTime(detail.start_time, detail.date)],['To',fmtTime(detail.end_time, detail.date)]].map(([k,v]) => (
                  <div key={k} style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#2554b8', textTransform:'uppercase', letterSpacing:0.8, marginBottom:3 }}>{k}</div>
                    <div style={{ fontWeight:700, color:'#0f1f45', fontSize:14 }}>{v}</div>
                  </div>
                ))}
                {/* Join Class button — online scheduled classes, available at any
                    time (no start-time gate). The link is resolved on click: a
                    Teams meeting is created on demand if it doesn't exist yet. */}
                {detail.mode === 'online' && detail.status === 'scheduled' && (
                  <button onClick={async () => {
                    const w = window.open('', '_blank');
                    try {
                      const r = await API.get(`/teams/join/${detail.id}`);
                      const url = r.data?.join_url;
                      if (url) { if (w) w.location.href = url; else window.location.href = url; }
                      else { if (w) w.close(); alert('Class link is not ready yet. Please try again in a moment.'); }
                    } catch (e) { if (w) w.close(); alert(e.response?.data?.detail || 'Class link is not ready yet. Please try again in a moment.'); }
                  }}
                    style={{ display:'block', width:'100%', padding:'12px', borderRadius:12, background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:800, fontSize:14, textAlign:'center', border:'none', cursor:'pointer', marginTop:10, boxShadow:'0 4px 14px rgba(22,163,74,0.35)', boxSizing:'border-box' }}>
                    🔗 Join Class Now
                  </button>
                )}
              </>
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   REQUEST FORMS (leave history)
═══════════════════════════════════════════════════ */
function RequestForms({ setActive }) {
  const [leaves, setLeaves] = useState([]);
  const load = useCallback(() => { API.get('/leave/my').then(r => setLeaves(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const statusStyle = (s) => ({ approved:'rgba(22,163,74,0.1)|#16a34a|rgba(22,163,74,0.2)', rejected:'rgba(220,38,38,0.1)|#dc2626|rgba(220,38,38,0.2)', pending:'rgba(217,119,6,0.1)|#d97706|rgba(217,119,6,0.2)' }[s]||'rgba(100,116,139,0.1)|#64748b|rgba(100,116,139,0.2)').split('|');

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
        <h3 style={{ fontWeight:800, fontSize:20, color:'#0f1f45' }}>My Submitted Requests</h3>
        <button className="s-btn" onClick={() => setActive('leave')} style={{ padding:'10px 22px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:'0 4px 14px rgba(37,84,184,0.3)' }}>+ New Request</button>
      </div>
      <div style={card({ padding:0, overflow:'hidden' })}>
        {leaves.length === 0 ? (
          <div style={{ textAlign:'center', padding:'52px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:56, marginBottom:14, opacity:0.5 }} className="floating">📋</div>
            <p style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>No requests submitted yet.</p>
            <button className="s-btn" onClick={() => setActive('leave')} style={{ marginTop:12, padding:'10px 24px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:'0 4px 14px rgba(37,84,184,0.3)' }}>Submit a Leave Request</button>
          </div>
        ) : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:460 }}>
            <thead><tr style={{ background:'#f8faff' }}>
              {['Type','From','To','Reason','Status'].map(h => (
                <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {leaves.map((l,i) => { const [bg,color,border]=statusStyle(l.status); return (
                <tr key={l.id} className="s-tr s-row" style={{ borderTop:'1px solid #f1f5f9', animationDelay:`${i*0.05}s` }}>
                  <td style={{ padding:'13px 20px', fontWeight:700, color:'#0f1f45', whiteSpace:'nowrap' }}>{l.leave_type}</td>
                  <td style={{ padding:'13px 20px', fontFamily:'monospace', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtDate(l.start_date)}</td>
                  <td style={{ padding:'13px 20px', fontFamily:'monospace', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtDate(l.end_date)}</td>
                  <td style={{ padding:'13px 20px', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#475569', fontSize:13 }}>{l.reason||'—'}</td>
                  <td style={{ padding:'13px 20px' }}><span style={{ padding:'4px 12px', borderRadius:20, fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', background:bg, color, border:`1px solid ${border}` }}>{l.status}</span></td>
                </tr>
              );})}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   LEAVE REQUEST FORM
═══════════════════════════════════════════════════ */
function LeaveRequestForm() {
  const [leaveType, setLeaveType] = useState('');
  const [form, setForm] = useState({ start_date:'', end_date:'', reason:'' });
  const [msg, setMsg] = useState('');
  const [affectedClasses, setAffectedClasses] = useState([]);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);
  const leaveTypes = [
    { id:'Sick Leave',     icon:'🤒', label:'Sick Leave',     color:'#dc2626', bg:'#fef2f2' },
    { id:'Vacation Leave', icon:'✈️', label:'Vacation Leave', color:'#2554b8', bg:'#eff6ff' },
    { id:'Personal Leave', icon:'👤', label:'Personal Leave', color:'#7c3aed', bg:'#f5f3ff' },
  ];

  // Fetch affected classes when dates change
  const checkAffectedClasses = async (start, end) => {
    if (!start || !end) { setAffectedClasses([]); setSelectedScheduleIds([]); return; }
    try {
      const r = await API.post('/leave/schedules-on-dates', buildDateRange(start, end));
      const list = r.data || [];
      setAffectedClasses(list);
      setSelectedScheduleIds(list.map(s => s.id)); // pre-select all
    } catch { setAffectedClasses([]); setSelectedScheduleIds([]); }
  };

  const buildDateRange = (start, end) => {
    const dates = [];
    let cur = new Date(start);
    const endD = new Date(end);
    while (cur <= endD) { dates.push(cur.toISOString().slice(0,10)); cur.setDate(cur.getDate()+1); }
    return dates;
  };

  const submit = async () => {
    if (!leaveType||!form.start_date||!form.end_date) { setMsg('Please select leave type and dates'); return; }
    if (affectedClasses.length > 0 && selectedScheduleIds.length === 0) {
      setMsg('Please select at least one class to cancel, or change dates if no cancellation needed'); return;
    }
    try {
      await API.post('/leave/', { leave_type:leaveType, ...form, schedule_ids: selectedScheduleIds });
      setMsg(`✓ Leave request submitted! ${selectedScheduleIds.length > 0 ? `${selectedScheduleIds.length} class(es) have been cancelled.` : ''}`);
      setLeaveType(''); setForm({ start_date:'', end_date:'', reason:'' }); setAffectedClasses([]); setSelectedScheduleIds([]);
    } catch (e) { setMsg(e.response?.data?.detail||'Failed to submit'); }
  };

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      <div style={{ maxWidth:660, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:30 }}>
          <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#2554b8,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 14px', boxShadow:'0 8px 24px rgba(37,84,184,0.3)' }}>📝</div>
          <h2 style={{ fontWeight:800, fontSize:24, color:'#0f1f45', marginBottom:6 }}>Leave Request</h2>
          <p style={{ color:'#64748b', fontSize:14 }}>Submit your leave application with ease</p>
        </div>

        <div style={{ background:'linear-gradient(135deg,#2554b8,#3b82f6)', borderRadius:14, padding:'16px 22px', marginBottom:22, display:'flex', alignItems:'center', gap:10, color:'#fff', fontWeight:700, fontSize:14 }}>
          👤 Student Leave Application
        </div>

        {msg && <div onClick={() => setMsg('')} style={{ padding:'12px 18px', borderRadius:10, fontSize:13, fontWeight:600, marginBottom:18, background:String(msg).startsWith('✓')?'rgba(22,163,74,0.08)':'rgba(220,38,38,0.08)', border:`1px solid ${String(msg).startsWith('✓')?'rgba(22,163,74,0.25)':'rgba(220,38,38,0.25)'}`, color:String(msg).startsWith('✓')?'#16a34a':'#dc2626', cursor:'pointer' }}>{msg}</div>}

        <div style={{ ...card(), marginBottom:18 }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, display:'block', marginBottom:14 }}>⏱ Leave Type</label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            {leaveTypes.map(lt => (
              <div key={lt.id} className={`s-leave-card ${leaveType===lt.id?'chosen':''}`} onClick={() => setLeaveType(lt.id)}
                style={{ border:`2px solid ${leaveType===lt.id?lt.color:'#e2e8f6'}`, borderRadius:14, padding:'20px 14px', textAlign:'center', background:leaveType===lt.id?lt.bg:'#fff', boxShadow:leaveType===lt.id?`0 6px 20px ${lt.color}22`:'none' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>{lt.icon}</div>
                <div style={{ fontSize:13, fontWeight:700, color:leaveType===lt.id?lt.color:'#0f1f45' }}>{lt.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={card()}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            {[['📅 Start Date','start_date'],['📅 End Date','end_date']].map(([label,key]) => (
              <div key={key}>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>{label}</label>
                <input type="date" value={form[key]} onChange={e => {
                  const updated = {...form,[key]:e.target.value};
                  setForm(updated);
                  checkAffectedClasses(updated.start_date, updated.end_date);
                }} className="s-form-inp"
                  style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }} />
              </div>
            ))}
          </div>
          {affectedClasses.length > 0 && (
            <div style={{ marginBottom:16, padding:'14px 16px', borderRadius:10, background:'rgba(220,38,38,0.06)', border:'1.5px solid rgba(220,38,38,0.2)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                <div style={{ fontWeight:700, color:'#dc2626', fontSize:13 }}>⚠️ Select which classes to cancel ({selectedScheduleIds.length}/{affectedClasses.length} selected):</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="button" onClick={() => setSelectedScheduleIds(affectedClasses.map(s => s.id))}
                    style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #fca5a5', background:'#fff', color:'#dc2626', fontSize:11, fontWeight:700, cursor:'pointer' }}>Select All</button>
                  <button type="button" onClick={() => setSelectedScheduleIds([])}
                    style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #cbd5e1', background:'#fff', color:'#64748b', fontSize:11, fontWeight:700, cursor:'pointer' }}>Clear</button>
                </div>
              </div>
              {affectedClasses.map((s) => {
                const checked = selectedScheduleIds.includes(s.id);
                return (
                  <label key={s.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:8, cursor:'pointer', background: checked ? 'rgba(220,38,38,0.1)' : 'transparent', marginBottom:3 }}>
                    <input type="checkbox" checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedScheduleIds(prev => [...prev, s.id]);
                        else setSelectedScheduleIds(prev => prev.filter(id => id !== s.id));
                      }} />
                    <span style={{ fontSize:12, color: checked ? '#0f1f45' : '#64748b', fontWeight: checked ? 600 : 500 }}>
                      {s.subject} — {fmtDate(s.date)} {fmtTime(s.start_time)} - {fmtTime(s.end_time)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ marginBottom:22 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6, display:'block', marginBottom:7 }}>📝 Reason for Leave</label>
            <textarea value={form.reason} onChange={e => setForm({...form,reason:e.target.value})} rows={4} placeholder="Please provide a detailed reason for your leave request..." className="s-form-inp"
              style={{ width:'100%', border:'1.5px solid #e2e8f6', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box' }} />
          </div>
          <button className="s-btn" onClick={submit}
            style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:15, boxShadow:'0 6px 20px rgba(37,84,184,0.35)' }}>
            Submit Leave Request →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SETTINGS
═══════════════════════════════════════════════════ */
function Settings() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { API.get('/auth/me').then(r => setProfile(r.data)).catch(() => {}); }, []);
  if (!profile) return <LoadingState />;

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      <div style={{ maxWidth:700 }}>
        <div style={card({ padding:0, overflow:'hidden', marginBottom:20 })}>
          {/* Profile header */}
          <div style={{ background:'linear-gradient(135deg,#1a3c8f,#2554b8)', padding:'28px 28px 0', position:'relative' }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:18, paddingBottom:24 }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(255,255,255,0.2)', border:'4px solid rgba(255,255,255,0.35)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, fontWeight:800, color:'#fff', flexShrink:0 }}>{profile.name?.[0]?.toUpperCase()}</div>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:3 }}>
                  <h3 style={{ color:'#fff', fontWeight:800, fontSize:20 }}>{profile.name}</h3>
                  {profile.uid && <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 12px', borderRadius:20, border:'1px solid rgba(255,255,255,0.3)' }}>{profile.uid}</span>}
                </div>
                <p style={{ color:'rgba(255,255,255,0.65)', fontSize:13, marginTop:2 }}>{profile.email}</p>
                <span style={{ display:'inline-block', background:'rgba(255,255,255,0.18)', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 12px', borderRadius:20, marginTop:6, border:'1px solid rgba(255,255,255,0.3)' }}>🎓 Student</span>
              </div>
            </div>
          </div>
          <div style={{ padding:'22px 28px' }}>
            <div style={{ marginBottom:20 }}>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:10 }}>Contact Details</h4>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                {[['Alternate Email',profile.alt_email||'Not provided'],['Address',profile.address||'Not provided'],['Contact Number',profile.phone||'Not provided']].map(([k,v]) => (
                  <div key={k} style={{ background:'#f8faff', padding:'13px 16px', borderRadius:10, border:'1px solid #eef2fa' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, marginBottom:10 }}>Academic and Package Details</h4>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[['Current Class',profile.class_name||'N/A'],['Selected Package',profile.package||'N/A']].map(([k,v]) => (
                  <div key={k} style={{ background:'#f8faff', padding:'13px 16px', borderRadius:10, border:'1px solid #eef2fa' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding:'11px 16px', background:'rgba(220,38,38,0.05)', borderRadius:10, border:'1px solid rgba(220,38,38,0.15)', fontSize:13, color:'#b91c1c', fontWeight:600 }}>
              ⚠️ Please contact the admin for any changes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HELP & SUPPORT
═══════════════════════════════════════════════════ */
function HelpSupport() {
  return (
    <div style={{ padding:'28px 32px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:440 }} className="s-page">
      <div style={{ ...card({ textAlign:'center', padding:52, maxWidth:500 }) }}>
        <div style={{ fontSize:66, marginBottom:16 }} className="floating">🤝</div>
        <h2 style={{ fontWeight:800, fontSize:22, color:'#0f1f45', marginBottom:8 }}>Help and Support</h2>
        <p style={{ color:'#64748b', marginBottom:30, lineHeight:1.8, fontSize:14 }}>
          If you need any assistance, feel free to reach out to us through the following channels. We are here to help!
        </p>
        <div style={{ background:'linear-gradient(135deg,#f0f5ff,#e8eeff)', borderRadius:18, padding:'24px 32px', textAlign:'left', border:'1px solid #dbeafe', marginBottom:22 }}>
          <h4 style={{ fontWeight:800, fontSize:14, color:'#0f1f45', marginBottom:16 }}>Contact Information</h4>
          {[['📧','Email','info@sapienceconnect.com'],['📞','Phone','+918078406608']].map(([icon,label,val]) => (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12, fontSize:14 }}>
              <span style={{ fontSize:20 }}>{icon}</span>
              <span style={{ color:'#64748b', fontWeight:600 }}>{label}:</span>
              <span style={{ color:'#2554b8', fontWeight:700 }}>{val}</span>
            </div>
          ))}
        </div>
        <button className="s-btn" onClick={() => window.open('mailto:info@sapienceconnect.com')}
          style={{ padding:'12px 32px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:14, boxShadow:'0 6px 20px rgba(37,84,184,0.35)' }}>
          Contact Us Now
        </button>
      </div>
    </div>
  );
}

/* ── Shared components ── */
function LoadingState() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:320 }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:46, height:46, border:'4px solid #dbeafe', borderTopColor:'#2554b8', borderRadius:'50%', margin:'0 auto 14px', animation:'spin 0.9s linear infinite' }} />
        <p style={{ color:'#64748b', fontWeight:600, fontSize:14 }}>Loading…</p>
      </div>
    </div>
  );
}
function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign:'center', padding:'56px 20px', color:'#94a3b8' }}>
      <div style={{ fontSize:50, marginBottom:14, opacity:0.6 }} className="floating">{icon}</div>
      <p style={{ fontWeight:600, fontSize:14 }}>{text}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STUDENT CHAT
═══════════════════════════════════════════════════ */
function StudentChat() {
  const [room, setRoom] = useState(null);
  const [members, setMembers] = useState([]);
  const [isMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    API.get('/chat/rooms').then(r => {
      const rooms = r.data || [];
      if (rooms.length > 0) {
        setRoom(rooms[0]);
        API.get(`/chat/room-info/${rooms[0].room_id}`).then(ri => setMembers(ri.data?.members || [])).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  return (
    <div style={{ padding: isMobile ? '12px' : '28px 32px' }} className="s-page">
      <div style={{ display:'flex', gap:18, height: isMobile ? 'calc(100vh - 120px)' : 600 }}>
        {/* Chat window */}
        <div style={{ flex:1, minWidth:0 }}>
          <ChatWindow roomId={room?.room_id || null} studentName={room?.student_name || 'My Chat'} />
        </div>
        {/* Members panel — hidden on mobile */}
        {!isMobile && (
          <div style={{ width:220, flexShrink:0, background:'#fff', borderRadius:20, border:'1px solid #e8edf8', boxShadow:'0 4px 24px rgba(37,84,184,0.07)', padding:'16px 18px', overflowY:'auto' }}>
            <div style={{ fontWeight:800, fontSize:13, color:'#0f1f45', marginBottom:14 }}>Room Members</div>
            {members.length === 0 ? (
              <div style={{ color:'#94a3b8', fontSize:12, textAlign:'center', padding:'20px 0' }}>Loading members…</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {members.map((m, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#2554b8,#60a5fa)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
                      {(m.name || 'U')[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:12, color:'#0f1f45' }}>{m.name}</div>
                      <div style={{ fontSize:10, color:'#94a3b8', textTransform:'capitalize' }}>{m.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STUDENT CLASS REQUEST
═══════════════════════════════════════════════════ */
function StudentClassRequest() {
  const [form, setForm] = useState({ subject: '', tutor_id: '', preferred_date: '', preferred_time: '', reason: '' });
  const [myRequests, setMyRequests] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/class-request/my').then(r => setMyRequests(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    API.get('/enrollment/').then(r => setEnrollments(r.data || [])).catch(() => {});
  }, [load]);

  // Unique subjects from active enrollments
  const enrolledSubjects = [...new Map(
    enrollments.filter(e => e.status !== 'inactive').map(e => [e.subject, e])
  ).values()];

  // When subject changes, auto-fill tutor from enrollment
  const handleSubjectChange = (subject) => {
    const matched = enrollments.find(e => e.subject === subject && e.status !== 'inactive');
    setForm(p => ({ ...p, subject, tutor_id: matched ? String(matched.tutor_id) : '' }));
  };

  // Tutor name for selected subject
  const selectedTutorName = (() => {
    if (!form.tutor_id) return null;
    const e = enrollments.find(en => String(en.tutor_id) === String(form.tutor_id) && en.subject === form.subject);
    return e?.tutor_name || null;
  })();

  const submit = async () => {
    if (!form.subject || !form.preferred_date || !form.preferred_time) {
      setMsg('Please fill in subject, date, and time');
      return;
    }
    try {
      const payload = { ...form, tutor_id: form.tutor_id ? parseInt(form.tutor_id) : null };
      await API.post('/class-request/', payload);
      setMsg('✓ Request submitted successfully!');
      setForm({ subject: '', tutor_id: '', preferred_date: '', preferred_time: '', reason: '' });
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error submitting request');
    }
  };

  const statusStyle = (s) => {
    const map = {
      pending:  { background:'rgba(217,119,6,0.1)',  color:'#d97706', border:'1px solid rgba(217,119,6,0.25)' },
      approved: { background:'rgba(22,163,74,0.1)',  color:'#16a34a', border:'1px solid rgba(22,163,74,0.25)' },
      rejected: { background:'rgba(220,38,38,0.1)',  color:'#dc2626', border:'1px solid rgba(220,38,38,0.25)' },
    };
    return map[s] || { background:'#f1f5f9', color:'#64748b', border:'1px solid #e2e8f0' };
  };

  return (
    <div style={{ padding:'28px 32px' }} className="s-page">
      {/* Submit form */}
      <div style={{ ...card(), marginBottom:24 }}>
        <h3 style={{ fontWeight:800, fontSize:15, color:'#0f1f45', marginBottom:16 }}>📋 Request a Class</h3>
        {msg && (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, background: msg.startsWith('✓') ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)', color: msg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight:700, fontSize:13, cursor:'pointer' }} onClick={() => setMsg('')}>
            {msg}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Subject</label>
            <select value={form.subject} onChange={e => handleSubjectChange(e.target.value)}
              style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, boxSizing:'border-box', fontFamily:'inherit', background:'#fff' }}>
              <option value="">— Select a subject —</option>
              {enrolledSubjects.map(e => <option key={e.subject} value={e.subject}>{e.subject}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Companion</label>
            <div style={{ padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, background:'#f8faff', color: selectedTutorName ? '#0f1f45' : '#94a3b8', fontWeight: selectedTutorName ? 700 : 400, minHeight:38, boxSizing:'border-box' }}>
              {selectedTutorName ? `✓ ${selectedTutorName}` : 'Auto-filled when subject selected'}
            </div>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Preferred Date</label>
            <input type="date" value={form.preferred_date} onChange={e => setForm(p => ({ ...p, preferred_date: e.target.value }))}
              style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, boxSizing:'border-box', fontFamily:'inherit' }} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Preferred Time</label>
            <TimePicker12h value={form.preferred_time} onChange={v => setForm(p => ({ ...p, preferred_time: v }))} style={{ flex:1 }} />
          </div>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>Reason (optional)</label>
          <textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
            rows={3} placeholder="Describe what you'd like to cover or why you're requesting this class…"
            style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
        </div>
        <button className="s-btn" onClick={submit}
          style={{ padding:'10px 26px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:'0 4px 14px rgba(37,84,184,0.3)' }}>
          Submit Request
        </button>
      </div>

      {/* My requests list */}
      <div style={{ ...card({ padding:0, overflow:'hidden' }) }}>
        <div style={{ padding:'16px 22px', borderBottom:'1px solid #eef2fa', fontWeight:800, fontSize:14, color:'#0f1f45' }}>
          My Requests ({myRequests.length})
        </div>
        {myRequests.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:10 }}>📋</div>
            <p style={{ fontWeight:600, fontSize:13 }}>No requests submitted yet</p>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8faff' }}>
                {['Subject','Date','Time','Reason','Status'].map(h => (
                  <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {myRequests.map(r => (
                <tr key={r.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'12px 18px', fontSize:13, color:'#1e293b', fontWeight:600 }}>{r.subject}</td>
                  <td style={{ padding:'12px 18px', fontSize:13, color:'#475569' }}>{r.preferred_date}</td>
                  <td style={{ padding:'12px 18px', fontSize:13, color:'#475569' }}>{r.preferred_time ? fmtTime(r.preferred_time) : '—'}</td>
                  <td style={{ padding:'12px 18px', fontSize:12, color:'#64748b', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.reason || '—'}</td>
                  <td style={{ padding:'12px 18px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, ...statusStyle(r.status) }}>{r.status}</span>
                    {r.status === 'reassigned' && r.reassigned_date && (
                      <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>New: {fmtDate(r.reassigned_date)}{r.reassigned_time ? ` ${fmtTime(r.reassigned_time)}` : ''}</div>
                    )}
                    {r.tutor_remarks && (
                      <div style={{ fontSize:11, color:'#475569', marginTop:3, fontStyle:'italic' }}>"{r.tutor_remarks}"</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HOMEWORK PAGE
═══════════════════════════════════════════════════ */
function HomeworkPage() {
  const [logs, setLogs] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    API.get('/class/logs').then(r => {
      const homework = r.data.filter(l => l.homework && l.status === 'completed');
      setLogs(homework);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const getStatus = (log) => {
    if (log.homework_due_date && log.homework_due_date < today) return 'overdue';
    return 'pending';
  };

  const statusStyle = (s) => ({
    overdue:   { bg:'rgba(220,38,38,0.1)',  color:'#dc2626', border:'rgba(220,38,38,0.25)',  label:'⚠ Overdue'  },
    pending:   { bg:'rgba(217,119,6,0.1)',  color:'#d97706', border:'rgba(217,119,6,0.25)',  label:'⏳ Pending'  },
  })[s] || { bg:'#f1f5f9', color:'#64748b', border:'#e2e8f0', label:'Pending' };

  return (
    <div className="s-page">
      <div style={card({ padding:20, marginBottom:20 })}>
        <h3 className="s-gradient-text" style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>📚 My Homework</h3>
        <p style={{ color:'#64748b', fontSize:13 }}>Your homework assignments. Share completed work with your Companion in chat.</p>
      </div>

      {logs.length === 0 ? (
        <div style={card({ padding:40, textAlign:'center' })}>
          <div style={{ fontSize:48, marginBottom:12 }}>📚</div>
          <p style={{ color:'#64748b', fontSize:15 }}>No homework assigned yet</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {logs.map(l => {
            const s = getStatus(l);
            const st = statusStyle(s);
            return (
              <div key={l.id} style={card({ padding:20, border:`1px solid ${st.border}` })}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:'#0f1f45', marginBottom:6 }}>{l.subject || '—'}</div>
                    <div style={{ fontSize:13, color:'#334155', marginBottom:8, lineHeight:1.5, wordBreak:'break-word', overflowWrap:'anywhere', whiteSpace:'pre-wrap' }}>{l.homework}</div>
                    <div style={{ display:'flex', gap:16, fontSize:12, color:'#64748b', flexWrap:'wrap' }}>
                      {l.schedule_date && <span>📅 Session: {l.schedule_date}</span>}
                      {l.homework_due_date && <span>⏰ Due: <strong style={{ color: s === 'overdue' ? '#dc2626' : '#0f1f45' }}>{l.homework_due_date}</strong></span>}
                      <span>👨‍🏫 {l.tutor_name || '—'}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, flexShrink:0 }}>
                    <span style={{ padding:'4px 14px', borderRadius:20, fontSize:12, fontWeight:700, background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>{st.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   FEEDBACK PAGE
═══════════════════════════════════════════════════ */
function FeedbackPage() {
  const [weekStatus, setWeekStatus] = useState({ submitted: false, week: '' });
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState({
    tutor_id: '', subject: '',
    topic_understanding: 0, difficulty_level: 0, teacher_support: 0, overall_experience: 0,
    parent_feedback: '', additional_notes: '', feedback_type: 'weekly',
  });
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('submit');

  useEffect(() => {
    API.get('/feedback/week-status').then(r => setWeekStatus(r.data)).catch(() => {});
    API.get('/enrollment/').then(r => setEnrollments(r.data || [])).catch(() => {});
    API.get('/feedback/my').then(r => setHistory(r.data || [])).catch(() => {});
  }, []);

  const tutorOptions = [...new Map(enrollments.map(e => [e.tutor_id, e])).values()];

  const StarRating = ({ label, field }) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:13, fontWeight:700, color:'#334155', display:'block', marginBottom:6 }}>{label}</label>
      <div style={{ display:'flex', gap:8 }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setForm(f => ({ ...f, [field]: n }))}
            style={{ width:40, height:40, borderRadius:10, border:`2px solid ${form[field] >= n ? '#f59e0b' : '#e2e8f6'}`, background: form[field] >= n ? 'rgba(245,158,11,0.1)' : '#f8fafc', cursor:'pointer', fontSize:20, transition:'all 0.15s', color: form[field] >= n ? '#f59e0b' : '#cbd5e1' }}>
            ★
          </button>
        ))}
      </div>
    </div>
  );

  const submit = async () => {
    if (form.feedback_type === 'weekly' && weekStatus.submitted) {
      setMsg('You have already submitted your weekly feedback for this week.'); return;
    }
    if (!form.topic_understanding || !form.teacher_support || !form.overall_experience) {
      setMsg('Please fill all required ratings'); return;
    }
    setLoading(true); setMsg('');
    try {
      await API.post('/feedback/', {
        tutor_id: form.tutor_id ? parseInt(form.tutor_id) : null,
        subject: form.subject || null,
        topic_understanding: form.topic_understanding,
        difficulty_level: form.difficulty_level || null,
        teacher_support: form.teacher_support,
        overall_experience: form.overall_experience,
        parent_feedback: form.parent_feedback || null,
        additional_notes: form.additional_notes || null,
        feedback_type: form.feedback_type,
      });
      setMsg('✓ Feedback submitted successfully! Thank you.');
      if (form.feedback_type === 'weekly') setWeekStatus(s => ({ ...s, submitted: true }));
      setForm(f => ({ ...f, topic_understanding:0, difficulty_level:0, teacher_support:0, overall_experience:0, parent_feedback:'', additional_notes:'' }));
      API.get('/feedback/my').then(r => setHistory(r.data || [])).catch(() => {});
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error submitting feedback');
    }
    setLoading(false);
  };

  return (
    <div className="s-page">
      <div style={card({ padding:20, marginBottom:20 })}>
        <h3 className="s-gradient-text" style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>⭐ Session Feedback</h3>
        <p style={{ color:'#64748b', fontSize:13 }}>Share your experience to help us improve your learning journey.</p>
        {weekStatus.submitted && (
          <div style={{ marginTop:12, padding:'10px 16px', borderRadius:10, background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.2)', color:'#16a34a', fontSize:13, fontWeight:600 }}>
            ✓ Weekly feedback submitted for {weekStatus.week}
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[['submit','Submit Feedback'],['history','My History']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:'9px 20px', borderRadius:10, border:'none', background: tab===t ? 'linear-gradient(135deg,#2554b8,#3b82f6)' : '#fff', color: tab===t ? '#fff' : '#64748b', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow: tab===t ? '0 4px 14px rgba(37,84,184,0.25)' : '0 1px 4px rgba(0,0,0,0.06)', transition:'all 0.2s' }}>
            {l}
          </button>
        ))}
      </div>

      {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom:16 }} onClick={() => setMsg('')}>{msg}</div>}

      {tab === 'submit' && (
        <div style={card({ padding:24, maxWidth:560 })}>
          <div style={{ display:'flex', gap:8, marginBottom:20 }}>
            {[['weekly','📋 Weekly (Mandatory)'],['optional','💡 Additional']].map(([t,l]) => (
              <button key={t} onClick={() => setForm(f => ({ ...f, feedback_type: t }))}
                style={{ flex:1, padding:'10px', borderRadius:10, border:`2px solid ${form.feedback_type===t ? '#2554b8' : '#e2e8f6'}`, background: form.feedback_type===t ? 'rgba(37,84,184,0.08)' : '#fff', color: form.feedback_type===t ? '#2554b8' : '#94a3b8', fontWeight:700, cursor:'pointer', fontSize:13, transition:'all 0.2s' }}>
                {l}
              </button>
            ))}
          </div>

          {enrollments.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:700, color:'#334155', display:'block', marginBottom:6 }}>Companion <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
              <select className="form-control" value={form.tutor_id} onChange={e => {
                const enr = enrollments.find(en => String(en.tutor_id) === e.target.value);
                setForm(f => ({ ...f, tutor_id: e.target.value, subject: enr?.subject || f.subject }));
              }}>
                <option value="">— Select Tutor —</option>
                {tutorOptions.map(e => <option key={e.tutor_id} value={e.tutor_id}>{e.tutor_name}</option>)}
              </select>
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:700, color:'#334155', display:'block', marginBottom:6 }}>Subject <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
            <input className="form-control" placeholder="e.g. Mathematics" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
          </div>

          <StarRating label="📖 Topic Understanding *" field="topic_understanding" />
          <StarRating label="🔥 Difficulty Level" field="difficulty_level" />
          <StarRating label="🤝 Teacher Support *" field="teacher_support" />
          <StarRating label="⭐ Overall Experience *" field="overall_experience" />

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:700, color:'#334155', display:'block', marginBottom:6 }}>👨‍👩‍👧 Parent Feedback <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
            <textarea className="form-control" rows={3} placeholder="Any feedback from parents about the sessions…" value={form.parent_feedback} onChange={e => setForm(f => ({ ...f, parent_feedback: e.target.value }))} />
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:13, fontWeight:700, color:'#334155', display:'block', marginBottom:6 }}>📝 Additional Notes <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
            <textarea className="form-control" rows={2} placeholder="Anything else you'd like to share…" value={form.additional_notes} onChange={e => setForm(f => ({ ...f, additional_notes: e.target.value }))} />
          </div>

          <button onClick={submit} disabled={loading} style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:15, boxShadow:'0 4px 16px rgba(37,84,184,0.35)', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Submitting…' : '⭐ Submit Feedback'}
          </button>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {history.length === 0 ? (
            <div style={card({ padding:40, textAlign:'center' })}>
              <div style={{ fontSize:48, marginBottom:12 }}>⭐</div>
              <p style={{ color:'#64748b', fontSize:15 }}>No feedback submitted yet</p>
            </div>
          ) : history.map(r => (
            <div key={r.id} style={card({ padding:18 })}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8, marginBottom:10 }}>
                <div>
                  <span style={{ fontWeight:700, fontSize:14, color:'#0f1f45' }}>{r.subject || 'General'}</span>
                  {r.tutor_name && <span style={{ color:'#64748b', fontSize:12, marginLeft:10 }}>👨‍🏫 {r.tutor_name}</span>}
                </div>
                <span style={{ fontSize:12, color:'#94a3b8' }}>{r.submitted_at?.slice(0,10)}</span>
              </div>
              <div style={{ display:'flex', gap:12, fontSize:13, flexWrap:'wrap' }}>
                {r.topic_understanding && <span>📖 Understanding: {'★'.repeat(r.topic_understanding)}</span>}
                {r.teacher_support && <span>🤝 Teacher: {'★'.repeat(r.teacher_support)}</span>}
                {r.overall_experience && <span>⭐ Overall: {'★'.repeat(r.overall_experience)}</span>}
              </div>
              {r.parent_feedback && <p style={{ fontSize:12, color:'#64748b', marginTop:8, fontStyle:'italic' }}>Parent: "{r.parent_feedback}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   EXAM SECTION
═══════════════════════════════════════════════════ */
function ExamSection({ onStartExam, examSessions }) {
  const [code, setCode]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [alreadyDone, setAlreadyDone] = useState(null); // title of already-completed exam

  const handleStart = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { setError('Please enter an exam code.'); return; }
    setLoading(true); setError(''); setAlreadyDone(null);
    try {
      const r = await API.get(`/exams/by-code/${trimmed}`);
      const examInfo = r.data;
      // Block re-entry if this exam was already submitted
      const done = (examSessions || []).find( // eslint-disable-line
        s => s.exam_id === examInfo.id && s.completed_at
      );
      if (done) {
        setAlreadyDone(examInfo.title);
        return;
      }
      onStartExam(examInfo);
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid exam code. Please check and try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth:480, margin:'60px auto', padding:'0 16px' }}>
      <div style={{ background:'#fff', borderRadius:24, padding:36, boxShadow:'0 4px 32px rgba(37,84,184,0.10)', textAlign:'center' }}>
        <div style={{ fontSize:52, marginBottom:12 }}>✏️</div>
        <h2 style={{ fontWeight:800, fontSize:22, color:'#0f1f45', marginBottom:8 }}>Attend Your Exam</h2>
        <p style={{ color:'#64748b', fontSize:14, lineHeight:1.6, marginBottom:28 }}>
          Enter the exam code provided by your tutor or admin to start your examination. Make sure you are in a quiet place with good lighting before starting.
        </p>
        <input
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); setAlreadyDone(null); }}
          onKeyDown={e => e.key === 'Enter' && handleStart()}
          placeholder="Enter exam code e.g. EXAM-ABCD1234"
          style={{ width:'100%', padding:'14px 16px', borderRadius:12, border:'2px solid #e2e8f0', fontSize:15, fontWeight:700, letterSpacing:1.5, textAlign:'center', outline:'none', boxSizing:'border-box', marginBottom:12 }}
        />
        {error && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{error}</p>}
        {alreadyDone && (
          <div style={{ padding:'12px 16px', borderRadius:12, background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.3)', marginBottom:12, textAlign:'left' }}>
            <span style={{ fontSize:14, fontWeight:700, color:'#16a34a' }}>✓ Exam Already Submitted</span>
            <p style={{ fontSize:12, color:'#15803d', marginTop:4 }}>You have already submitted <strong>{alreadyDone}</strong>. Results will be shared by your Companion.</p>
          </div>
        )}
        {!alreadyDone && (
          <button onClick={handleStart} disabled={loading}
            style={{ width:'100%', padding:'14px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#7c3aed,#a855f7)', color:'#fff', fontWeight:800, fontSize:15, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow:'0 6px 20px rgba(124,58,237,0.4)' }}>
            {loading ? 'Verifying...' : '✏️ Start Exam'}
          </button>
        )}
        <div style={{ marginTop:20, padding:16, background:'#f8faff', borderRadius:12, textAlign:'left' }}>
          <p style={{ fontWeight:700, fontSize:12, color:'#475569', marginBottom:8 }}>Before you start:</p>
          <ul style={{ margin:0, padding:'0 0 0 16px', color:'#64748b', fontSize:12, lineHeight:2 }}>
            <li>Allow camera and microphone access when prompted</li>
            <li>Keep your answer sheets ready</li>
            <li>Do not switch tabs during the exam</li>
            <li>AI proctoring will monitor throughout</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ForcePasswordChange() {
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!newPw || newPw.length < 8) { setMsg('Password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setMsg('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await API.post('/auth/student-set-password', { new_password: newPw });
      localStorage.setItem('force_password_change', '0');
      window.location.reload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to set password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a3c8f 0%, #142e6e 100%)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 40, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔐</div>
          <div style={{ fontWeight: 800, fontSize: 22, color: '#1a3c8f' }}>Set Your Password</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Your account requires a new password before you can continue.</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>New Password</label>
          <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            placeholder="Min. 8 characters"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Confirm Password</label>
          <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            placeholder="Repeat password"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {msg && <div style={{ fontSize: 13, color: msg.startsWith('✅') ? '#16a34a' : '#dc2626', marginBottom: 14 }}>{msg}</div>}
        <button onClick={submit} disabled={loading}
          style={{ width: '100%', padding: '12px', background: '#1a3c8f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Saving…' : 'Set Password & Continue'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════ */
export default function Student() {
  const forcePwChange = localStorage.getItem('force_password_change') === '1';
  if (forcePwChange) return <ForcePasswordChange />;

  const [active, setActive] = useState('dashboard');
  const [academicInitTab, setAcademicInitTab] = useState('attendance');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [examData, setExamData] = useState(null);
  const [examSessions, setExamSessions] = useState([]);
  const name = localStorage.getItem('name') || 'Student';

  const refreshExamSessions = useCallback(() => {
    API.get('/exams/student/sessions').then(r => setExamSessions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refreshExamSessions(); }, [refreshExamSessions]);

  // One-click exam start used by the Schedule detail panel + Academic Details
  // exam list. Saves students from copying the code to a separate "Attend Exam"
  // page. Mirrors ExamSection.handleStart's validation: fetches the exam by
  // code and blocks re-entry if it was already submitted.
  const quickStartExam = useCallback(async (code) => {
    if (!code) return;
    try {
      const r = await API.get(`/exams/by-code/${code.toUpperCase()}`);
      const examInfo = r.data;
      const done = (examSessions || []).find(s => s.exam_id === examInfo.id && s.completed_at);
      if (done) {
        alert(`You have already submitted "${examInfo.title}".`);
        return;
      }
      setExamData(examInfo);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not start exam. Please check the code or contact your Companion.');
    }
  }, [examSessions]);

  // Deep-link from push notifications (?nav=chat etc.) and SW postMessage
  useEffect(() => {
    const applyNav = (target) => {
      if (!target) return;
      setActive(target);
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
    };
    const p = new URLSearchParams(window.location.search);
    applyNav(p.get('nav'));
    const onMsg = (e) => {
      if (e.data?.type === 'notification-click' && e.data?.url) {
        try {
          const u = new URL(e.data.url, window.location.origin);
          applyNav(u.searchParams.get('nav'));
        } catch (_) {}
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);
  const [unreadRooms, refreshUnread] = useUnreadChats();
  const unreadCount = Object.values(unreadRooms).filter(Boolean).length;
  const [cycleEndPopup, setCycleEndPopup] = useState(false);
  const [cycleEndDays, setCycleEndDays] = useState(null);
  const [paymentPopup, setPaymentPopup] = useState(false);  // false | { hours }

  useEffect(() => {
    API.get('/payments/my-hours').then(r => {
      const data = r.data;
      if (data?.days_until_end !== null && data?.days_until_end !== undefined && data.days_until_end >= 0 && data.days_until_end <= 3) {
        const todayKey = new Date().toDateString();
        const shownKey = `cycle_popup_${todayKey}`;
        if (!localStorage.getItem(shownKey)) {
          setCycleEndDays(data.days_until_end);
          setCycleEndPopup(true);
          localStorage.setItem(shownKey, '1');
        }
      }
      // Payment reminder popup: shown once per session if no payment & not muted
      if (data?.has_unpaid_bill && !data?.payment_popup_muted) {
        const sessionKey = 'pay_popup_shown';
        if (!sessionStorage.getItem(sessionKey)) {
          setPaymentPopup({ hours: data.pending_hours || 0 });
          sessionStorage.setItem(sessionKey, '1');
        }
      }
    }).catch(() => {});
  }, []);

  const titles = {
    dashboard:  { h:'Dashboard',           p:`Welcome back, ${name}` },
    profile:    { h:'Profile',             p:'Your personal details' },
    enrollment: { h:'Enrollment Details',  p:'Your assigned companions and subjects' },
    payment:    { h:'Payment Information', p:'Fee and transaction details' },
    academic:   { h:'Academic Details',    p:'Attendance, progress evaluation and test results' },
    schedule:   { h:'Schedules',           p:'Your upcoming and past class sessions' },
    request:    { h:'Request Forms',       p:'View and manage your submitted requests' },
    leave:      { h:'Leave Request Form',  p:'Submit a leave application' },
    help:       { h:'Help & Support',      p:'Contact us for assistance' },
    chat:       { h:'Chat',               p:'Message your Companion and admin' },
    classreq:   { h:'Class Request',       p:'Request an extra or rescheduled class' },
    homework:   { h:'Homework',            p:'Your pending and submitted homework assignments' },
    feedback:   { h:'Give Feedback',       p:'Share your weekly session feedback' },
    materials:  { h:'Study Materials',    p:'Browse and download study materials' },
    exam:       { h:'Attend Exam',         p:'Enter your exam code to begin' },
  };

  const views = {
    dashboard:  <Dashboard setActive={setActive} setAcademicInitTab={setAcademicInitTab} />,
    profile:    <Profile />,
    enrollment: <EnrollmentDetails />,
    payment:    <PaymentInformation />,
    academic:   <AcademicDetails initTab={academicInitTab} examSessions={examSessions} onStartExam={quickStartExam} />,
    schedule:   <Schedules examSessions={examSessions} onStartExam={quickStartExam} />,
    request:    <RequestForms setActive={setActive} />,
    leave:      <LeaveRequestForm />,
    help:       <HelpSupport />,
    chat:       <StudentChat />,
    classreq:   <StudentClassRequest />,
    homework:   <HomeworkPage />,
    feedback:   <FeedbackPage />,
    exam:       <ExamSection onStartExam={setExamData} examSessions={examSessions} />,
    materials:  <UserMaterials role="student" />,
  };

  // Full-screen ExamRoom overrides normal layout
  if (examData) {
    return <ExamRoom examData={examData} onComplete={() => { setExamData(null); setActive('dashboard'); refreshExamSessions(); }} />;
  }

  return (
    <div className="app-shell" style={{ background:'#f0f4fb', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
      {/* Cycle end popup */}
      {cycleEndPopup && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000 }} onClick={() => setCycleEndPopup(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:36, width:380, textAlign:'center', boxShadow:'0 30px 80px rgba(0,0,0,0.2)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ fontSize:52, marginBottom:12 }}>⏰</div>
            <h3 style={{ fontSize:20, fontWeight:800, color:'#0f1f45', marginBottom:8 }}>
              {cycleEndDays === 0 ? 'Cycle Ends Today!' : `Cycle Ends in ${cycleEndDays} Day${cycleEndDays > 1 ? 's' : ''}!`}
            </h3>
            <p style={{ color:'#64748b', marginBottom:24, fontSize:14 }}>
              Your current hour cycle is ending soon. Please contact your admin to renew and start a new cycle.
            </p>
            <button onClick={() => setCycleEndPopup(false)} style={{ padding:'11px 32px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(37,84,184,0.35)' }}>
              Got it
            </button>
          </div>
        </div>
      )}
      {/* Payment reminder popup */}
      {paymentPopup && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000 }} onClick={() => setPaymentPopup(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:36, width:400, textAlign:'center', boxShadow:'0 30px 80px rgba(0,0,0,0.2)', animation:'bounceIn 0.3s ease' }}>
            <div style={{ fontSize:52, marginBottom:12 }}>💳</div>
            <h3 style={{ fontSize:20, fontWeight:800, color:'#0f1f45', marginBottom:8 }}>Fee Reminder</h3>
            <p style={{ color:'#64748b', marginBottom:24, fontSize:14, lineHeight:1.6 }}>
              Hi! Just a gentle reminder that a payment is pending on your bill{paymentPopup.hours > 0 ? ` (${paymentPopup.hours}h)` : ''}. Kindly ensure the payment is made so classes continue without interruption. Thank you! 🙏
            </p>
            <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
              <button onClick={() => { setPaymentPopup(false); setActive('payment'); }} style={{ padding:'11px 28px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:'0 4px 14px rgba(37,84,184,0.35)' }}>
                View Payments
              </button>
              <button onClick={() => setPaymentPopup(false)} style={{ padding:'11px 28px', borderRadius:12, border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar active={active} setActive={setActive} unreadCount={unreadCount} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="main-content" style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', minWidth:0 }}>
        {/* Mobile topbar */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#fff', borderBottom:'1px solid #e8edf8', position:'sticky', top:0, zIndex:5 }} className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <img src="/logo.png" alt="logo" style={{ height:28, objectFit:'contain' }} />
            <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>Sapience Edu Connect</span>
          </div>
        </div>
        {/* Top bar */}
        <div className="desktop-topbar" style={{ background:'#fff', borderBottom:'1px solid #e8edf8', padding:'16px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:5, boxShadow:'0 2px 12px rgba(37,84,184,0.06)' }}>
          <div>
            <h2 style={{ fontWeight:800, fontSize:20, color:'#0f1f45', letterSpacing:-0.4 }}>{titles[active].h}</h2>
            <p style={{ fontSize:12, color:'#64748b', marginTop:2, fontWeight:500 }}>{titles[active].p}</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:13, color:'#64748b', fontWeight:600 }}>Welcome, {name}</span>
            <div onClick={() => setActive('profile')} style={{ width:38, height:38, borderRadius:'50%', background:'linear-gradient(135deg,#dbeafe,#bfdbfe)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, border:'2px solid #bfdbfe', cursor:'pointer', fontWeight:800, color:'#2554b8', transition:'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow='0 4px 14px rgba(37,84,184,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
              {name[0]?.toUpperCase()}
            </div>
          </div>
        </div>
        <div key={active} style={{ flex:1 }}>
          {views[active]}
        </div>
      </div>
    </div>
  );
}
